'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { ReactFlow, Background, Controls, Panel, Position, BaseEdge, EdgeLabelRenderer, applyNodeChanges, applyEdgeChanges, getSmoothStepPath, useReactFlow, useStore, MarkerType, } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { ToggleGroup, ToggleGroupItem } from './components/ui/toggle-group';
import { ArrowRight, Check, ChevronDown, ChevronRight, Flag, MessageSquare, MousePointerClick, PanelRight, Ban, PhoneForwarded, Plus, Redo2, ClipboardList, Trash2, TriangleAlert, Undo2, Volume2, Zap, } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AutofitMenu, MiniMapCard } from './CanvasControls';
import { SMOOTH_STEP_OFFSET, SMOOTH_STEP_RADIUS, bendAxis, clampBend, exitInsetFromRight, } from './line-geometry';
import { EDGE_STROKE_WIDTH, HIGHLIGHT_OUTLINE } from './editor-styles';
import { cn } from './lib/utils';
import * as historyStack from './history';
import { DEFAULT_GRAPH_VOICE, INCOMING_COMMUNICATION_EVENT_NAME, POST_CALL_ACTION_TYPES, VOICE_OPTIONS, channelsForTriggerVariables, destinationChoicesFor, nodeTypeRunsOn, pickedDestinationChoice, ActionType, } from '@super/shared-types';
import { END_NODE_ID, TRIGGER_NODE_ID, eventLabel, flowIdOf, flowStartNodeId, isEntryPill, isFlowStartNodeId, isImplicitSource, isSelectableNodeType, newId, } from './schema';
import { listOrder, moveAmongSiblings, moveRow, orderSiblings, settleRows, } from './arrow-order';
import { summarize, valueLabelFrom, } from './equation-options';
import { ArrowPanel } from './panels/equation/ArrowPanel';
import { FormNode } from './nodes/FormNode';
import { FlowStartNode, renumberFlows, } from './nodes/FlowStartNode';
import { MultiHandle } from './nodes/MultiHandle';
import { CARD_BOTTOM_INSET, CARD_RIGHT_INSET, ENTRY_HANDLE_INSET, EntryHandle, STEP_CARD_WIDTH, NodeUnreachableTip, TRANSITION_ROW_HEIGHT, TRANSITION_ROW_PITCH, TransitionList, transitionListHeight, } from './nodes/TransitionList';
import { ActionNodePanel, memberLabel } from './panels/ActionNodePanel';
import { FieldHelp } from './panels/FieldHelp';
import { FlowStartPanel } from './panels/FlowStartPanel';
import { WatchEventsPicker } from './panels/WatchEventsPicker';
import { CrossCuttingFlowList, } from './panels/CrossCuttingFlowList';
import { TransferNodePanel } from './panels/TransferNodePanel';
import { FormPanel } from './panels/FormPanel';
import { NodeKnowledgePicker } from './panels/NodeKnowledgePicker';
import { NodeToolsPicker } from './panels/NodeToolsPicker';
import { SignalsPanel } from './panels/SignalsPanel';
import { useGraphFrontendComponentAllowlist } from './GraphFrontendComponentAllowlistContext';
import { resolveAsyncRef } from './async-resource';
import { getVariableTextEditor, } from './panels/VariableTextEditor';
import { FlowMetaContext, defaultSourceSide, defaultTargetSide, slotOffsetPercent, sourceSlotFor, DRAFT_HANDLE_PREFIX, ENTRY_HANDLE_ID, } from './FlowEditorContext';
/** Target slot id for an edge. A node card has one entry handle; the End
 *  pill keeps a slot per (source, target) pair, plus the side (left or top)
 *  the edge enters on. */
function targetSlotFor(edge) {
    if (edge.target !== END_NODE_ID)
        return ENTRY_HANDLE_ID;
    return `tgt-${edge.side}:${edge.source}->${edge.target}`;
}
function triggerLabelOf(trigger, options) {
    if (!trigger)
        return null;
    if (trigger.eventName === INCOMING_COMMUNICATION_EVENT_NAME) {
        return 'Incoming communication';
    }
    const match = options.find(option => option.eventName === trigger.eventName);
    return match?.name ?? trigger.eventName;
}
/**
 * What the chosen trigger can reach, and where each destination is read from.
 * A trigger the editor was not told about offers nothing, so the author is
 * asked to pick again rather than shown a guess.
 */
function triggerReachOf(trigger, options) {
    if (trigger?.eventName === INCOMING_COMMUNICATION_EVENT_NAME) {
        // Whoever reaches the company brings their own address, so there is no
        // event field to name and every channel is open.
        const reach = { eventName: trigger.eventName };
        return {
            channels: channelsForTriggerVariables(reach),
            choices: destinationChoicesFor(reach),
        };
    }
    const match = trigger
        ? options.find(option => option.eventName === trigger.eventName)
        : undefined;
    if (!match)
        return { channels: [], choices: [] };
    return {
        channels: channelsForTriggerVariables(match),
        choices: destinationChoicesFor(match),
    };
}
function startChannelLabel(step, choices) {
    const picked = pickedDestinationChoice(choices, step);
    if (!picked)
        return '';
    // The whole choice, variable included: a trigger can declare several
    // numbers, so the channel alone does not say where the call goes. Inbound
    // names no variable and its label is the channel on its own.
    return `Channel: ${picked.label}`;
}
// Stable identity so the default does not retrigger memoised children.
const EMPTY_KNOWLEDGE_BASES = [];
const EMPTY_TRIGGERS = [];
const EMPTY_SCRATCHPAD_PATHS = [];
/**
 * True when a step patch would only tweak prose in an in-focus field (title,
 * conversation prompt, say text, or form item text) AND the top of the
 * history stack already captures the pre-edit state of the same step. Caller
 * skips the snapshot so a 10-keystroke edit lands as one undo entry — the
 * next structural change (toggle, add/delete item, blur-and-focus-elsewhere
 * via a non-text patch) always snapshots on its own.
 */
function isPureTextCoalesce(currentNodes, stepId, patch, history) {
    const keys = Object.keys(patch);
    if (keys.length !== 1)
        return false;
    const key = keys[0];
    if (key !== 'title' &&
        key !== 'text' &&
        key !== 'prompt' &&
        key !== 'items' &&
        key !== 'signals') {
        return false;
    }
    const current = currentNodes.find(n => n.id === stepId);
    if (!current)
        return false;
    if (history.past.length === 0)
        return false;
    const top = history.past[history.past.length - 1];
    const topNode = top?.nodes.find(n => n.id === stepId);
    if (!topNode)
        return false;
    const currentData = current.data;
    const topData = topNode.data;
    if (key === 'items' || key === 'signals') {
        // Form items and a conversation node's signals are both lists the author
        // types into one block at a time; a text-only edit coalesces the same way.
        const nextItems = patch[key];
        const prevItems = currentData[key];
        const topItems = topData[key];
        if (!nextItems || !prevItems || !topItems)
            return false;
        if (prevItems.length !== nextItems.length)
            return false;
        if (topItems.length !== prevItems.length)
            return false;
        for (let i = 0; i < prevItems.length; i += 1) {
            if (!sameItemShapeIgnoringText(prevItems[i], nextItems[i]))
                return false;
            if (!sameItemShapeIgnoringText(topItems[i], prevItems[i]))
                return false;
        }
        return true;
    }
    // title / text / prompt — scalar prose. The top of past must already
    // represent the step's non-prose shape; otherwise this is the first edit
    // in the session and we need to capture it.
    return topShapeMatchesCurrentExcept(topData, currentData, key);
}
/** Typing a draft's name joins the undo step its first keystroke made. */
function isDraftLabelCoalesce(drafts, draftId, patch, history) {
    const keys = Object.keys(patch);
    if (keys.length !== 1 || keys[0] !== 'label')
        return false;
    const top = history.past[history.past.length - 1];
    const before = top?.drafts.find(d => d.id === draftId);
    const now = drafts.find(d => d.id === draftId);
    if (!before || !now)
        return false;
    return JSON.stringify(before.condition) === JSON.stringify(now.condition);
}
function sameItemShapeIgnoringText(a, b) {
    if (a.id !== b.id || a.kind !== b.kind)
        return false;
    const { text: _at, ...aRest } = a;
    const { text: _bt, ...bRest } = b;
    return JSON.stringify(aRest) === JSON.stringify(bRest);
}
function topShapeMatchesCurrentExcept(top, current, skipKey) {
    const topKeys = Object.keys(top).sort();
    const currentKeys = Object.keys(current).sort();
    if (topKeys.join('|') !== currentKeys.join('|'))
        return false;
    for (const k of topKeys) {
        if (k === skipKey)
            continue;
        if (JSON.stringify(top[k]) !== JSON.stringify(current[k]))
            return false;
    }
    return true;
}
function dataOf(step) {
    if (step.type === 'say') {
        return { kind: 'say', title: step.title, text: step.text };
    }
    if (step.type === 'form') {
        return {
            kind: 'form',
            title: step.title,
            items: step.items,
            knowledgeBaseIds: step.knowledgeBaseIds,
            excludedKnowledgeBaseIds: step.excludedKnowledgeBaseIds,
            tools: step.tools,
        };
    }
    if (step.type === 'start_conversation') {
        return {
            kind: 'start_conversation',
            title: step.title,
            channels: step.channels,
            destinationVariable: step.destinationVariable ?? null,
        };
    }
    if (step.type === 'end_conversation') {
        return {
            kind: 'end_conversation',
            title: step.title,
            signOff: step.signOff ? { ...step.signOff } : null,
        };
    }
    if (step.type === 'action') {
        return {
            kind: 'action',
            title: step.title,
            actionType: step.actionType,
            nodeActionGroupId: step.nodeActionGroupId,
        };
    }
    if (step.type === 'transfer') {
        return {
            kind: 'transfer',
            title: step.title,
            workflowActionId: step.workflowActionId,
        };
    }
    return {
        kind: 'conversation',
        title: step.title,
        prompt: step.prompt,
        knowledgeBaseIds: step.knowledgeBaseIds,
        excludedKnowledgeBaseIds: step.excludedKnowledgeBaseIds,
        tools: step.tools,
        signals: step.signals,
        speaksOnEntry: step.speaksOnEntry,
    };
}
function stepOf(id, data) {
    if (data.kind === 'say') {
        return { id, type: 'say', title: data.title, text: data.text };
    }
    if (data.kind === 'form') {
        return {
            id,
            type: 'form',
            title: data.title,
            items: data.items,
            knowledgeBaseIds: data.knowledgeBaseIds,
            excludedKnowledgeBaseIds: data.excludedKnowledgeBaseIds,
            tools: data.tools,
        };
    }
    if (data.kind === 'end_conversation') {
        return {
            id,
            type: 'end_conversation',
            title: data.title,
            signOff: data.signOff ? { ...data.signOff } : null,
        };
    }
    if (data.kind === 'action') {
        return {
            id,
            type: 'action',
            title: data.title,
            actionType: data.actionType,
            nodeActionGroupId: data.nodeActionGroupId,
        };
    }
    if (data.kind === 'transfer') {
        return {
            id,
            type: 'transfer',
            title: data.title,
            workflowActionId: data.workflowActionId,
        };
    }
    if (data.kind === 'start_conversation') {
        return {
            id,
            type: 'start_conversation',
            title: data.title,
            channels: data.channels,
            destinationVariable: data.destinationVariable ?? null,
        };
    }
    return {
        id,
        type: 'conversation',
        title: data.title,
        prompt: data.prompt,
        knowledgeBaseIds: data.knowledgeBaseIds,
        excludedKnowledgeBaseIds: data.excludedKnowledgeBaseIds,
        tools: data.tools,
        signals: data.signals,
        speaksOnEntry: data.speaksOnEntry,
    };
}
/* ---------- canvas pieces ---------- */
/**
 * Where the entry arrow leaves from. It carries the trigger, which is what
 * starts the graph, so it names that rather than reading "Start" — the Start
 * conversation node the arrow points at owns that word.
 */
function StartNode({ id, selected }) {
    const { edgeHighlightedNodeIds, triggerLabel } = useContext(FlowMetaContext);
    const highlight = edgeHighlightedNodeIds.has(id);
    return (_jsxs("div", { className: "flex items-center gap-1.5 rounded-full border-2 border-foreground bg-card px-3 py-1.5 shadow-sm", style: selected || highlight ? HIGHLIGHT_OUTLINE : undefined, children: [_jsx(Zap, { className: cn('h-3.5 w-3.5', triggerLabel ? 'text-primary' : 'text-muted-foreground/60') }), _jsx("span", { className: cn('text-xs', 
                // Nothing picked reads as a placeholder, the same way an
                // unconfigured node card does.
                triggerLabel ? 'font-medium' : 'text-muted-foreground/60 italic'), children: triggerLabel ?? 'Select trigger' }), _jsx(MultiHandle, { nodeId: id, handleType: "source", nodeSelected: !!selected, position: Position.Right, side: "right" }), _jsx(MultiHandle, { nodeId: id, handleType: "source", nodeSelected: !!selected, position: Position.Bottom, side: "bottom" })] }));
}
function EndNode({ id, selected }) {
    const { edgeHighlightedNodeIds } = useContext(FlowMetaContext);
    const highlight = edgeHighlightedNodeIds.has(id);
    return (_jsxs("div", { className: "flex items-center gap-1.5 rounded-full border-2 border-foreground bg-card px-3 py-1.5 shadow-sm", style: selected || highlight ? HIGHLIGHT_OUTLINE : undefined, children: [_jsx(Ban, { className: "h-3.5 w-3.5 text-muted-foreground" }), _jsx("span", { className: "text-xs font-medium text-muted-foreground", children: "End call" }), _jsx(MultiHandle, { nodeId: id, handleType: "target", nodeSelected: !!selected, position: Position.Left, side: "left" }), _jsx(MultiHandle, { nodeId: id, handleType: "target", nodeSelected: !!selected, position: Position.Top, side: "top" })] }));
}
/** Canvas-card header for an action node: title + fan-out count. The
 *  summary panel on the right reads the same shape, so the two surfaces
 *  stay in step. */
function actionNodeHeader(title, group) {
    const base = title || 'Untitled';
    if (!group)
        return base;
    const count = group.members.length;
    const label = count === 1 ? 'action' : 'actions';
    return `${base} • ${count} ${label}`;
}
function StepNode({ id, data, selected }) {
    const step = data;
    const { activeNodeId, previousNodeId, edgeHighlightedNodeIds, nodeActionGroupsById, transferActionReferencesById, implicitSources, reachable, triggerChoices, channelsReachingNode, } = useContext(FlowMetaContext);
    const unreachable = !reachable.has(id);
    // A step a conversation cannot run. The node says so rather than the Start
    // node listing what it strands: the step is where the author can act on it.
    const onChannels = channelsReachingNode.get(id) ?? [];
    const wrongChannel = !unreachable &&
        onChannels.length > 0 &&
        // `kind` carries the same strings as a graph node's `type`.
        !nodeTypeRunsOn(step.kind, onChannels);
    const isActive = activeNodeId === id;
    const justLeft = previousNodeId === id && activeNodeId !== id;
    const tools = step.kind === 'conversation' ? step.tools : [];
    const hasTools = tools.length > 0;
    const visibleTools = tools.slice(0, 3);
    const overflow = tools.length - visibleTools.length;
    const actionGroup = step.kind === 'action'
        ? nodeActionGroupsById[step.nodeActionGroupId]
        : undefined;
    const actionPreview = step.kind === 'action'
        ? !step.nodeActionGroupId
            ? 'Pick an action'
            : actionGroup === undefined
                ? 'Loading…'
                : actionGroup === null
                    ? 'Action was deleted'
                    : actionGroup.name
                        ? actionGroup.name
                        : `${actionGroup.members.length} Action${actionGroup.members.length === 1 ? '' : 's'}`
        : '';
    const transferReference = step.kind === 'transfer'
        ? resolveAsyncRef(transferActionReferencesById, step.workflowActionId)
        : null;
    const transferPreview = step.kind === 'transfer'
        ? !step.workflowActionId
            ? 'Pick a transfer action'
            : transferReference?.kind === 'loading'
                ? 'Loading…'
                : transferReference?.kind === 'missing'
                    ? 'Transfer action was deleted'
                    : (transferReference?.value.name ?? 'Unnamed transfer')
        : '';
    const preview = step.kind === 'say'
        ? step.text
        : step.kind === 'form'
            ? (step.items[0]?.text ?? '')
            : step.kind === 'end_conversation'
                ? step.signOff?.mode === 'exact' &&
                    step.signOff.exactText.trim() !== ''
                    ? step.signOff.exactText
                    : 'The call ends here.'
                : step.kind === 'action'
                    ? actionPreview
                    : step.kind === 'transfer'
                        ? transferPreview
                        : step.kind === 'start_conversation'
                            ? // Empty until a channel is picked, so the card falls through
                                // to its placeholder and reads as unfinished.
                                startChannelLabel(step, triggerChoices)
                            : step.prompt;
    const placeholder = step.kind === 'say'
        ? 'Add the line it says…'
        : step.kind === 'form'
            ? 'Add questions…'
            : step.kind === 'end_conversation'
                ? ''
                : step.kind === 'action'
                    ? ''
                    : step.kind === 'transfer'
                        ? ''
                        : step.kind === 'start_conversation'
                            ? 'Configure…'
                            : 'Add a prompt…';
    const Icon = step.kind === 'say'
        ? Volume2
        : step.kind === 'form'
            ? ClipboardList
            : step.kind === 'end_conversation'
                ? Ban
                : step.kind === 'action'
                    ? Zap
                    : step.kind === 'transfer'
                        ? PhoneForwarded
                        : step.kind === 'start_conversation'
                            ? Flag
                            : MessageSquare;
    const activeShadow = [
        '0 0 0 2px rgba(34,197,94,0.55), 0 0 18px rgba(34,197,94,0.20)',
        '0 0 0 2px rgba(34,197,94,0.95), 0 0 28px rgba(34,197,94,0.42)',
        '0 0 0 2px rgba(34,197,94,0.55), 0 0 18px rgba(34,197,94,0.20)',
    ];
    const idleShadow = '0 0 0 0px rgba(34,197,94,0)';
    return (_jsx(NodeUnreachableTip, { nodeId: id, children: _jsxs(motion.div, { animate: {
                boxShadow: isActive ? activeShadow : idleShadow,
                scale: justLeft ? [1, 0.98, 1] : 1,
            }, transition: isActive
                ? { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }
                : justLeft
                    ? { duration: 0.35, ease: 'easeOut' }
                    : { duration: 0.25, ease: 'easeOut' }, className: cn('w-[220px] rounded-xl border-2 bg-card px-3 py-2.5 shadow-sm transition-shadow hover:shadow-md', (step.kind === 'transfer' &&
                (!step.workflowActionId ||
                    transferReference?.kind === 'missing')) ||
                (step.kind === 'action' &&
                    (!step.nodeActionGroupId || actionGroup === null))
                ? cn('border-destructive', !reachable.has(id) && 'border-dashed')
                : reachable.has(id)
                    ? 'border-foreground'
                    : // Not reachable: the same dashed amber as a row that is not
                        // connected.
                        'border-dashed border-amber-500'), style: selected || edgeHighlightedNodeIds.has(id)
                ? HIGHLIGHT_OUTLINE
                : undefined, children: [_jsx(EntryHandle, { nodeId: id }), _jsxs("div", { className: "flex items-center gap-2 min-w-0", children: [_jsx(Icon, { className: cn('h-3.5 w-3.5 flex-shrink-0', (step.kind === 'transfer' &&
                                (!step.workflowActionId ||
                                    transferReference?.kind === 'missing')) ||
                                (step.kind === 'action' &&
                                    (!step.nodeActionGroupId || actionGroup === null))
                                ? 'text-destructive'
                                : 'text-primary') }), _jsx("span", { className: "text-sm font-medium truncate", children: step.kind === 'end_conversation'
                                ? 'End conversation'
                                : step.kind === 'transfer'
                                    ? step.title || 'Transfer'
                                    : step.kind === 'action'
                                        ? actionNodeHeader(step.title, actionGroup)
                                        : step.title || 'Untitled' })] }), step.kind !== 'end_conversation' && (_jsx("p", { className: cn('mt-1 text-xs truncate', (step.kind === 'transfer' &&
                        (!step.workflowActionId ||
                            transferReference?.kind === 'missing')) ||
                        (step.kind === 'action' &&
                            (!step.nodeActionGroupId || actionGroup === null))
                        ? 'text-destructive'
                        : preview
                            ? 'text-muted-foreground'
                            : 'text-muted-foreground/60 italic'), children: preview || placeholder })), hasTools && (_jsxs("div", { className: "mt-1.5 flex flex-wrap gap-1", children: [visibleTools.map(({ tool }) => (_jsx("span", { className: "rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary", children: tool.replace(/_/g, ' ').toLowerCase() }, tool))), overflow > 0 && (_jsxs("span", { className: "rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground", children: ["+", overflow] }))] })), step.kind === 'conversation' && step.signals.length > 0 && (_jsx("div", { className: "mt-1.5", children: _jsxs("span", { className: "rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground", children: ["listens for ", step.signals.length] }) })), (unreachable || wrongChannel) && (_jsxs("span", { className: "mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-500", children: [_jsx(TriangleAlert, { className: "h-3 w-3 shrink-0" }), unreachable
                            ? 'not reachable from Start'
                            : 'Eligible for phone only'] })), _jsx(TransitionList, { nodeId: id, unconditional: implicitSources.has(id) })] }) }));
}
// FocusHandle wires imperative viewport helpers back to the FlowEditor's
// parent through refs. Lives inside <ReactFlow> so useReactFlow has the
// flow's context.
function FocusHandle({ focusStartRef, focusNodeRef, }) {
    const reactFlow = useReactFlow();
    useEffect(() => {
        const centerOn = (id) => {
            const node = reactFlow.getNode(id);
            if (!node) {
                reactFlow.fitView({ padding: 0.25, duration: 500 });
                return;
            }
            const width = node.measured?.width ??
                node.width ??
                (node.type === 'step' || node.type === 'form'
                    ? STEP_CARD_WIDTH
                    : PILL_LAYOUT_WIDTH);
            const height = node.measured?.height ??
                node.height ??
                (node.type === 'step' || node.type === 'form'
                    ? STEP_LAYOUT_HEIGHT
                    : PILL_LAYOUT_HEIGHT);
            reactFlow.setCenter(node.position.x + width / 2, node.position.y + height / 2, { duration: 500, zoom: 1 });
        };
        focusStartRef.current = () => centerOn(TRIGGER_NODE_ID);
        focusNodeRef.current = centerOn;
        return () => {
            focusStartRef.current = null;
            focusNodeRef.current = null;
        };
    }, [reactFlow, focusStartRef, focusNodeRef]);
    return null;
}
// AutoPan lives inside <ReactFlow> so `useReactFlow` has the flow's context.
// On active-node transition, it smooth-scrolls the viewport to keep the
// highlight on screen — invaluable on narrow viewports during a live call.
function AutoPan({ activeNodeId }) {
    const reactFlow = useReactFlow();
    useEffect(() => {
        if (!activeNodeId)
            return;
        const node = reactFlow.getNode(activeNodeId);
        if (!node)
            return;
        const width = node.measured?.width ?? node.width ?? STEP_CARD_WIDTH;
        const height = node.measured?.height ?? node.height ?? 80;
        reactFlow.setCenter(node.position.x + width / 2, node.position.y + height / 2, { duration: 500, zoom: reactFlow.getZoom() });
    }, [activeNodeId, reactFlow]);
    return null;
}
function ArrowEdge(props) {
    const { id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, selected, markerEnd, } = props;
    // Mirror of the edge-selected → node-highlighted rule. When either end is
    // selected (clicked in the canvas), the edge paints green so the operator
    // can see which conditions touch the node they're inspecting.
    const touchesSelected = useStore(s => {
        for (const n of s.nodes) {
            if (!n.selected)
                continue;
            if (n.id === source || n.id === target)
                return true;
        }
        return false;
    });
    const { bendArrow } = useContext(FlowMetaContext);
    const { screenToFlowPosition } = useReactFlow();
    // A row's dot on a card's bottom (top to bottom) sits inside the card, and
    // the card draws the line from the dot to its bottom edge. The arrow starts
    // there, so it does not run hidden under the card.
    const cardBottom = useStore(state => {
        if (sourcePosition !== Position.Bottom)
            return null;
        const node = state.nodeLookup.get(source);
        const height = node?.measured.height;
        return node && height ? node.internals.positionAbsolute.y + height : null;
    });
    const startY = cardBottom === null ? sourceY : Math.max(sourceY, cardBottom);
    const bend = props.data?.bend;
    const slide = bendAxis({ x: sourceX, y: startY }, sourcePosition, { x: targetX, y: targetY }, targetPosition);
    const axis = slide?.axis ?? null;
    // Kept far enough from both ends that each corner gets the full radius.
    const center = bend && slide
        ? slide.axis === 'x'
            ? clampBend(bend.x, sourceX, targetX, slide.forward)
            : clampBend(bend.y, startY, targetY, slide.forward)
        : undefined;
    // Pure smoothstep from live handle coords. Drag follows for free because
    // sourceX/sourceY update when nodes move. A dragged bend moves the middle
    // segment along its one free axis.
    const [path, gripX, gripY] = getSmoothStepPath({
        sourceX,
        sourceY: startY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: SMOOTH_STEP_RADIUS,
        // On a forward line between facing handles a stub adds no corner, only
        // takes room from the middle ones, so drop it there.
        offset: slide?.forward ? 0 : SMOOTH_STEP_OFFSET,
        ...(center !== undefined && axis === 'x' ? { centerX: center } : {}),
        ...(center !== undefined && axis === 'y' ? { centerY: center } : {}),
    });
    const onGripPointerDown = (event) => {
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        bendArrow(id, 'start');
    };
    const onGripPointerMove = (event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId))
            return;
        bendArrow(id, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
    };
    const onGripPointerUp = (event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId))
            return;
        event.currentTarget.releasePointerCapture(event.pointerId);
        bendArrow(id, 'end');
    };
    return (_jsxs(_Fragment, { children: [_jsx(BaseEdge, { id: id, path: path, markerEnd: markerEnd, interactionWidth: 20, style: {
                    stroke: touchesSelected || selected ? 'var(--edge-highlight)' : undefined,
                    strokeWidth: touchesSelected || selected ? 2 : EDGE_STROKE_WIDTH,
                } }), axis && (selected || touchesSelected) && (_jsx(EdgeLabelRenderer, { children: _jsx("div", { className: cn('nodrag nopan absolute h-3 w-3 rounded-full border-2 border-[color:var(--edge-highlight)] bg-background shadow-sm', axis === 'x' ? 'cursor-ew-resize' : 'cursor-ns-resize'), style: {
                        transform: `translate(-50%, -50%) translate(${gripX}px, ${gripY}px)`,
                        pointerEvents: 'all',
                    }, title: "Drag to move the line. Double-click to reset.", onPointerDown: onGripPointerDown, onPointerMove: onGripPointerMove, onPointerUp: onGripPointerUp, onDoubleClick: event => {
                        event.stopPropagation();
                        bendArrow(id, 'start');
                        bendArrow(id, null);
                        bendArrow(id, 'end');
                    } }) }))] }));
}
/** The Start conversation node on the canvas, which the entry edge leads to. */
function startConversationIdOf(nodes) {
    const start = nodes.find(n => n.data?.kind ===
        'start_conversation');
    return start?.id ?? null;
}
const nodeTypes = {
    start: StartNode,
    flowStart: FlowStartNode,
    step: StepNode,
    form: FormNode,
    end: EndNode,
};
const edgeTypes = { arrow: ArrowEdge };
/* ---------- layout ---------- */
// ELK lays out the whole graph — Start, every Step, End — as one layered DAG.
// Each layer is a column (LR) or row (TB), so decision-tree depth maps 1:1 to
// visual rank. See README.md for the tuning knobs.
const STEP_LAYOUT_HEIGHT = 96;
const PILL_LAYOUT_WIDTH = 96;
const PILL_LAYOUT_HEIGHT = 40;
const elk = new ELK();
/** How long the first layout waits for React Flow to measure the cards. */
const MEASURE_WAIT_MS = 300;
/** The sizes React Flow measured, by node id. Unmeasured nodes are absent. */
function measuredSizes(nodes) {
    const sizes = new Map();
    for (const node of nodes) {
        const width = node.measured?.width;
        const height = node.measured?.height;
        if (width && height)
            sizes.set(node.id, { width, height });
    }
    return sizes;
}
async function layoutPositions(state, direction = 'LR', options) {
    // Compute both source and target slot lists per node from state.arrows so
    // ELK's ports match the handles MultiHandle paints. Source slots are one
    // per arrow; target slots are unique incoming source ids. Both sides use the same (i+1)/(n+1) percent spacing.
    // ELK uses the same per-edge slot ids as the DOM handles so routing lands
    // on the exact pixel the invisible handle sits at. No deduplication —
    // each edge is its own slot.
    const layoutStepById = new Map(state.steps.map(step => [step.id, step]));
    const sideOf = (source) => sourceSideFor(source, layoutStepById, defaultSourceSide(direction));
    const sourceSlotsByNode = new Map();
    const targetSlotsByNode = new Map();
    for (const arrow of state.arrows) {
        const sSlot = sourceSlotFor({
            source: arrow.source,
            target: arrow.target,
            side: sideOf(arrow.source),
        });
        const tSlot = targetSlotFor({
            source: arrow.source,
            target: arrow.target,
            side: defaultTargetSide(direction),
        });
        const sList = sourceSlotsByNode.get(arrow.source) ?? [];
        if (!sList.includes(sSlot))
            sList.push(sSlot);
        sourceSlotsByNode.set(arrow.source, sList);
        const tList = targetSlotsByNode.get(arrow.target) ?? [];
        if (!tList.includes(tSlot))
            tList.push(tSlot);
        targetSlotsByNode.set(arrow.target, tList);
    }
    const nodeShape = (id) => {
        const isPill = isEntryPill(id) || id === END_NODE_ID;
        const rowed = layoutStepById.has(id);
        const sSlots = sourceSlotsByNode.get(id) ?? [];
        const tSlots = targetSlotsByNode.get(id) ?? [];
        // The card's rows as TransitionList lists them: arrows and drafts.
        const listed = rowed && !isImplicitSource(id, layoutStepById)
            ? listOrder(sSlots.map(slotId => ({ id: slotId })), options?.drafts?.filter(draft => draft.source === id) ?? [])
            : sSlots.map(slotId => ({ id: slotId, draft: false }));
        const rowOf = (slotId) => listed.findIndex(row => !row.draft && row.id === slotId);
        const measured = options?.sizes?.get(id);
        const width = measured?.width ?? (isPill ? PILL_LAYOUT_WIDTH : STEP_CARD_WIDTH);
        const height = measured?.height ??
            (isPill
                ? PILL_LAYOUT_HEIGHT
                : STEP_LAYOUT_HEIGHT +
                    (rowed
                        ? // A say or end-conversation card shows its fixed row even
                            // before it has an arrow.
                            transitionListHeight(Math.max(listed.length, isImplicitSource(id, layoutStepById) ? 1 : 0))
                        : 0));
        const ports = [];
        // Port positions mirror the clustered slot layout MultiHandle paints so
        // ELK routes edges through the exact pixels the handles live at. The
        // slotOffsetPercent helper returns a percent-of-side value centered on
        // the midpoint — same formula on both sides, same port locations as
        // the DOM handles.
        tSlots.forEach((slotId, index) => {
            if (slotId === ENTRY_HANDLE_ID) {
                ports.push(direction === 'TB'
                    ? {
                        id: `${id}:${slotId}`,
                        x: ENTRY_HANDLE_INSET,
                        y: 0,
                        layoutOptions: { 'elk.port.side': 'NORTH' },
                    }
                    : {
                        id: `${id}:${slotId}`,
                        x: 0,
                        y: ENTRY_HANDLE_INSET,
                        layoutOptions: { 'elk.port.side': 'WEST' },
                    });
                return;
            }
            const t = slotOffsetPercent(index, tSlots.length) / 100;
            ports.push(direction === 'LR'
                ? {
                    id: `${id}:${slotId}`,
                    x: 0,
                    y: t * height,
                    layoutOptions: { 'elk.port.side': 'WEST' },
                }
                : {
                    id: `${id}:${slotId}`,
                    x: t * width,
                    y: 0,
                    layoutOptions: { 'elk.port.side': 'NORTH' },
                });
        });
        sSlots.forEach((slotId, index) => {
            if (rowed) {
                // Mirrors TransitionList: on the right at the row's center (LR), or
                // on the card's bottom under the row's staggered dot (TB).
                ports.push(direction === 'TB'
                    ? {
                        id: `${id}:${slotId}`,
                        x: width -
                            CARD_RIGHT_INSET -
                            exitInsetFromRight(rowOf(slotId), width - 2 * CARD_RIGHT_INSET),
                        y: height,
                        layoutOptions: { 'elk.port.side': 'SOUTH' },
                    }
                    : {
                        id: `${id}:${slotId}`,
                        x: width,
                        y: height -
                            CARD_BOTTOM_INSET -
                            (listed.length - 1 - rowOf(slotId)) * TRANSITION_ROW_PITCH -
                            TRANSITION_ROW_HEIGHT / 2,
                        layoutOptions: { 'elk.port.side': 'EAST' },
                    });
                return;
            }
            const t = slotOffsetPercent(index, sSlots.length) / 100;
            ports.push(direction === 'LR'
                ? {
                    id: `${id}:${slotId}`,
                    x: width,
                    y: t * height,
                    layoutOptions: { 'elk.port.side': 'EAST' },
                }
                : {
                    id: `${id}:${slotId}`,
                    x: t * width,
                    y: height,
                    layoutOptions: { 'elk.port.side': 'SOUTH' },
                });
        });
        return { width, height, ports };
    };
    const pinned = options?.pinnedPositions;
    const makeNode = (id) => {
        const node = {
            id,
            ...nodeShape(id),
            layoutOptions: { 'elk.portConstraints': 'FIXED_POS' },
        };
        const seed = pinned?.get(id);
        if (seed) {
            node.x = seed.x;
            node.y = seed.y;
        }
        return node;
    };
    const children = [
        makeNode(TRIGGER_NODE_ID),
        makeNode(END_NODE_ID),
        ...state.flowStarts.map(flow => makeNode(flowStartNodeId(flow.id))),
        ...state.steps.map(step => makeNode(step.id)),
    ];
    const nodeIds = new Set(children.map(c => c.id));
    const edges = state.arrows
        .filter(a => nodeIds.has(a.source) && nodeIds.has(a.target))
        .map(a => {
        const slot = sourceSlotFor({
            source: a.source,
            target: a.target,
            side: sideOf(a.source),
        });
        const tSlot = targetSlotFor({
            source: a.source,
            target: a.target,
            side: defaultTargetSide(direction),
        });
        return {
            id: a.id,
            sources: [`${a.source}:${slot}`],
            targets: [`${a.target}:${tSlot}`],
        };
    });
    // BRANDES_KOEPF with BALANCED alignment gives the clean grid-row look —
    // siblings at the same depth land on the same cross-axis coordinate.
    const graph = {
        id: 'root',
        layoutOptions: {
            'elk.algorithm': 'layered',
            'elk.direction': direction === 'LR' ? 'RIGHT' : 'DOWN',
            // Tight spacing so a typical decision-tree fits one screen without the
            // Autofit zoom shrinking the text past readable. The LR layer gap
            // dominates fit on wide-and-shallow trees — kept small so 4-5 layers
            // land inside a ~1200px editor pane.
            'elk.layered.spacing.nodeNodeBetweenLayers': direction === 'TB' ? '60' : '60',
            'elk.spacing.nodeNode': direction === 'TB' ? '32' : '24',
            'elk.spacing.edgeNode': '16',
            // In pinned mode we want ELK to honor the current node coordinates —
            // INTERACTIVE strategies read x/y to pick layers and in-layer order
            // without reshuffling. Fresh layouts use the regular BRANDES_KOEPF /
            // LAYER_SWEEP / GREEDY_MODEL_ORDER combination that gives the clean
            // tree-row look.
            ...(pinned
                ? {
                    'elk.layered.layering.strategy': 'INTERACTIVE',
                    'elk.layered.crossingMinimization.strategy': 'INTERACTIVE',
                    'elk.layered.nodePlacement.strategy': 'INTERACTIVE',
                    'elk.layered.cycleBreaking.strategy': 'INTERACTIVE',
                }
                : {
                    'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
                    'elk.layered.nodePlacement.bk.fixedAlignment': 'BALANCED',
                    'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
                    // GREEDY_MODEL_ORDER + considerModelOrder lets ELK honor the
                    // order steps come in — fewer long-haul layer jumps for edges
                    // whose source and target are near each other in the graph.
                    'elk.layered.cycleBreaking.strategy': 'GREEDY_MODEL_ORDER',
                    'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
                }),
            'elk.padding': '[top=20,left=20,bottom=20,right=20]',
        },
        children,
        edges,
    };
    const laid = await elk.layout(graph);
    const nodes = new Map();
    for (const child of laid.children ?? []) {
        if (typeof child.x === 'number' && typeof child.y === 'number') {
            // ELK can return fractional coordinates; round so handle positions and
            // smoothstep anchor pixels stay integer-aligned (sub-pixel values show
            // up as 1px edge "hitches" next to the node).
            nodes.set(child.id, {
                x: Math.round(child.x),
                y: Math.round(child.y),
            });
        }
    }
    // Capture the full ELK route per edge: startPoint, every bendPoint, then
    // endPoint. ArrowEdge uses this verbatim so entry/exit segments stay
    // axis-aligned — anchoring to React Flow's handle coordinates instead would
    // introduce a diagonal entry whenever the handle's y (or x in TB mode)
    // doesn't match ELK's chosen routing lane, which reads as a small kink.
    const edgeLayouts = new Map();
    for (const edge of laid.edges ?? []) {
        const points = [];
        for (const section of edge.sections ?? []) {
            if (section.startPoint) {
                points.push({ x: section.startPoint.x, y: section.startPoint.y });
            }
            for (const bp of section.bendPoints ?? []) {
                points.push({ x: bp.x, y: bp.y });
            }
            if (section.endPoint) {
                points.push({ x: section.endPoint.x, y: section.endPoint.y });
            }
        }
        if (points.length === 0)
            continue;
        edgeLayouts.set(edge.id, { route: points });
    }
    return { nodes, edges: edgeLayouts };
}
// `overrides` seeds positions verbatim (e.g. from a shared URL or an in-flight
// re-layout). Ids missing from `overrides` get a (0,0) placeholder; the ELK
// effect in FlowEditor fills them in once layout resolves.
function buildNodes(state, overrides, _direction = 'LR') {
    const posOf = (id) => overrides?.get(id) ?? { x: 0, y: 0 };
    // The legacy End pseudo-pill is only drawn when the author actually routes
    // to it. Templates that end on explicit end_conversation nodes skip the
    // pill; otherwise it floats disconnected on the canvas.
    const needsEndPill = state.arrows.some(a => a.target === END_NODE_ID);
    return [
        {
            id: TRIGGER_NODE_ID,
            type: 'start',
            position: posOf(TRIGGER_NODE_ID),
            data: {},
            deletable: false,
        },
        ...state.flowStarts.map((flow, order) => ({
            id: flowStartNodeId(flow.id),
            type: 'flowStart',
            position: posOf(flowStartNodeId(flow.id)),
            data: { events: flow.events, order },
        })),
        ...state.steps.map(step => ({
            id: step.id,
            type: step.type === 'form' ? 'form' : 'step',
            position: posOf(step.id),
            data: dataOf(step),
            // Every graph opens on Start, and the editor places it. Deleting it
            // would leave the trigger's arrow pointing at nothing.
            ...(step.type === 'start_conversation' ? { deletable: false } : {}),
        })),
        ...(needsEndPill
            ? [
                {
                    id: END_NODE_ID,
                    type: 'end',
                    position: posOf(END_NODE_ID),
                    data: {},
                    deletable: false,
                },
            ]
            : []),
    ];
}
/** The side an arrow leaves its source from. Every step lists its arrows as
 *  rows, and a row's arrow leaves from the right; the Start and flow pills
 *  use `otherwise`. */
function sourceSideFor(source, stepById, otherwise) {
    return stepById.has(source) ? 'right' : otherwise;
}
/** Side the edge enters on, from edge data, with the current flow
 *  direction's default as the fallback. */
function resolveTargetSide(data, direction) {
    return data?.targetSide ?? defaultTargetSide(direction);
}
/** Which source side the operator targeted on drop, parsed out of the
 *  React Flow Connection's `sourceHandle` id. Handle ids are prefixed
 *  `src-right:…` or `src-bottom:…` on all source handles. */
function parseSourceSideFromHandle(handleId, fallback) {
    if (!handleId)
        return fallback;
    if (handleId.startsWith('src-right'))
        return 'right';
    if (handleId.startsWith('src-bottom'))
        return 'bottom';
    return fallback;
}
function parseTargetSideFromHandle(handleId, fallback) {
    if (!handleId)
        return fallback;
    if (handleId.startsWith('tgt-left'))
        return 'left';
    if (handleId.startsWith('tgt-top'))
        return 'top';
    return fallback;
}
// The entry arrow is an edge like any other: it carries the id the API stores
// and waits for `session_started`. Only its source pill makes it special.
//
// sourceHandle is the computed slot id (see sourceSlotFor); targetHandle is
// the single-input slot on every node. The node renderer looks up the slot
// list from context and renders handles with these ids, which guarantees
// each edge's referenced handle exists at the moment React Flow validates
// the edge — no handle-id race (React Flow error #008).
function buildEdges(state, initialBends, direction) {
    const stepById = new Map(state.steps.map(step => [step.id, step]));
    return state.arrows.map(arrow => {
        const data = {
            ...(arrow.condition ? { condition: arrow.condition } : {}),
            ...(arrow.label ? { label: arrow.label } : {}),
            bend: initialBends?.[arrow.id] ?? undefined,
        };
        const sourceSide = sourceSideFor(arrow.source, stepById, data?.sourceSide ?? defaultSourceSide(direction));
        const targetSide = resolveTargetSide(data, direction);
        return {
            id: arrow.id,
            source: arrow.source,
            target: arrow.target,
            sourceHandle: sourceSlotFor({
                source: arrow.source,
                target: arrow.target,
                side: sourceSide,
            }),
            targetHandle: targetSlotFor({
                source: arrow.source,
                target: arrow.target,
                side: targetSide,
            }),
            type: 'arrow',
            animated: arrow.source === TRIGGER_NODE_ID,
            // Every graph opens on the trigger's arrow into Start. Removing it
            // would leave the graph with no way in, which the contract refuses.
            deletable: arrow.source !== TRIGGER_NODE_ID,
            data,
            markerEnd: { type: MarkerType.ArrowClosed },
        };
    });
}
/** The canvas arrow a React Flow edge stands for. */
function arrowOf(edge) {
    const data = edge.data;
    return {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        ...(data?.condition ? { condition: data.condition } : {}),
        ...(data?.label ? { label: data.label } : {}),
    };
}
/* ---------- right panel ---------- */
/**
 * One control for adding a node, because the choice is which kind, not which
 * button. A conversation node talks with the caller; a say node delivers one
 * line and moves on.
 */
function TabButton({ active, label, onClick, }) {
    return (_jsx("button", { type: "button", onClick: onClick, className: cn('flex-1 px-3 py-2 text-xs font-medium transition-colors border-b-2', active
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted-foreground hover:text-foreground'), children: label }));
}
function AddNodeMenu({ onAdd }) {
    const allowlist = useGraphFrontendComponentAllowlist();
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);
    useEffect(() => {
        if (!open)
            return;
        const onPointerDown = (event) => {
            const target = event.target;
            if (target && !containerRef.current?.contains(target))
                setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape')
                setOpen(false);
        };
        window.addEventListener('mousedown', onPointerDown);
        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('mousedown', onPointerDown);
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);
    const choose = (kind) => {
        setOpen(false);
        onAdd(kind);
    };
    const choices = [
        {
            kind: 'conversation',
            Icon: MessageSquare,
            label: 'Conversation node',
            hint: 'The agent talks with the caller, following a prompt.',
        },
        {
            kind: 'say',
            Icon: Volume2,
            label: 'Say node',
            hint: 'The agent delivers one line, then moves on.',
        },
        {
            kind: 'form',
            Icon: ClipboardList,
            label: 'Form node',
            hint: 'A block of structured questions the agent asks in order.',
        },
        {
            kind: 'end_conversation',
            Icon: Ban,
            label: 'End conversation node',
            hint: 'Hangs up the call when the graph reaches it.',
        },
        {
            kind: 'transfer',
            Icon: PhoneForwarded,
            label: 'Transfer node',
            hint: 'Dials a human, then routes on the outcome (success, declined, voicemail, timeout, failed).',
        },
        ...(allowlist.allowedActionTypes.length > 0
            ? [
                {
                    kind: 'action',
                    Icon: Zap,
                    label: 'Action node',
                    hint: 'The agent runs an action and routes on the outcome.',
                },
            ]
            : []),
    ];
    return (_jsxs("div", { ref: containerRef, className: "relative", children: [_jsxs(Button, { type: "button", size: "sm", className: "h-8 gap-1 shadow-sm", "aria-haspopup": "menu", "aria-expanded": open, onClick: () => setOpen(current => !current), children: [_jsx(Plus, { className: "h-3.5 w-3.5" }), "Add node", _jsx(ChevronDown, { className: cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180') })] }), open && (_jsx("div", { role: "menu", className: "absolute left-0 top-9 z-20 w-64 overflow-hidden rounded-md border border-border bg-popover shadow-md", children: choices.map(choice => (_jsxs("button", { type: "button", role: "menuitem", className: "flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-muted/60 border-b last:border-b-0", onClick: () => choose(choice.kind), children: [_jsx(choice.Icon, { className: "mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" }), _jsxs("span", { className: "min-w-0", children: [_jsx("span", { className: "block text-sm font-medium", children: choice.label }), _jsx("span", { className: "block text-xs text-muted-foreground", children: choice.hint })] })] }, choice.kind))) }))] }));
}
function KnowledgeBasePicker({ available, selected, onChange, }) {
    if (available.length === 0)
        return null;
    return (_jsx("div", { className: "border rounded-md max-h-44 overflow-y-auto", children: available.map(base => {
            const checked = selected.includes(base.id);
            return (_jsxs("div", { className: cn('flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0', checked ? 'bg-primary/5' : 'hover:bg-muted/50'), onClick: () => onChange(checked
                    ? selected.filter(id => id !== base.id)
                    : [...selected, base.id]), children: [_jsx("div", { className: cn('flex h-4 w-4 items-center justify-center rounded border-2 transition-colors flex-shrink-0', checked
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/30'), children: checked && _jsx(Check, { className: "h-2.5 w-2.5" }) }), _jsx("div", { className: "text-sm truncate", children: base.name })] }, base.id));
        }) }));
}
function ExplainerPanel({ persona, voice, knowledgeBaseIds, watchEvents, knowledgeBases, firstStepTitle, variables, flows, onFlows, onChange, }) {
    const rows = [
        { icon: MessageSquare, text: 'A node is what the agent does and says.' },
        {
            icon: ArrowRight,
            text: 'A line moves the call on, when its condition holds.',
        },
        {
            icon: MousePointerClick,
            text: 'Click any node or line to edit it, then Test call to try it.',
        },
    ];
    return (_jsxs("div", { className: "space-y-5", children: [_jsxs("div", { className: "space-y-4", children: [_jsx("h3", { className: "text-sm font-medium", children: "Graph settings" }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Persona" }), (() => {
                                const Editor = getVariableTextEditor();
                                return (_jsx(Editor, { value: persona, onValueChange: v => onChange({ persona: v }), placeholder: "You are Sam, a friendly leasing assistant\u2026", variables: variables, className: "block w-full min-h-[72px] resize-y border rounded-md px-2 py-1.5", rows: 4 }));
                            })(), _jsx(FieldHelp, { children: "Who the agent is. Every conversation node reads it before its own prompt." })] }), knowledgeBases.length > 0 && (_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Knowledge" }), _jsx(KnowledgeBasePicker, { available: knowledgeBases, selected: knowledgeBaseIds, onChange: ids => onChange({ knowledgeBaseIds: ids }) }), _jsx(FieldHelp, { children: "Every conversation node uses these unless it opts out." })] })), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Watch intents" }), _jsx(WatchEventsPicker, { selected: watchEvents, onChange: next => onChange({ watchEvents: next }) }), _jsx(FieldHelp, { children: "On every caller turn, the scratchpad detector infers these intents. Any node can route on them via a plain event condition." })] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Cross-cutting flows" }), _jsx(CrossCuttingFlowList, { flows: flows, actions: onFlows }), _jsx(FieldHelp, { children: "A flow starts from any node once all of its events have happened. Each flow starts once per call. A flow higher in the list can interrupt a flow below it." })] }), _jsxs("div", { className: "space-y-1", children: [_jsx(Label, { className: "text-xs", children: "First node" }), _jsx("p", { className: "text-xs", children: firstStepTitle ?? (_jsx("span", { className: "text-amber-600 dark:text-amber-500", children: "Not set. Drag from Start to a node." })) })] })] }), _jsxs("div", { className: "space-y-4 border-t pt-4", children: [_jsx("h3", { className: "text-sm font-medium", children: "Voice settings" }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", htmlFor: "graph-voice", children: "Voice" }), _jsx("select", { id: "graph-voice", className: "w-full h-8 rounded-md border bg-background px-2 text-xs", value: voice ?? DEFAULT_GRAPH_VOICE, onChange: e => onChange({ voice: e.target.value }), children: VOICE_OPTIONS.map(option => (_jsxs("option", { value: option.id, children: [option.label, " \u2014 ", option.description] }, option.id))) }), _jsx(FieldHelp, { children: "How the agent sounds on every line of the call." })] })] }), _jsxs("div", { className: "space-y-2.5 border-t pt-4", children: [_jsx("h3", { className: "text-sm font-medium", children: "How this works" }), rows.map((row, i) => (_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: "flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-semibold flex-shrink-0", children: i + 1 }), _jsx(row.icon, { className: "h-3.5 w-3.5 text-primary mt-1 flex-shrink-0" }), _jsx("p", { className: "text-xs text-muted-foreground leading-relaxed", children: row.text })] }, i)))] })] }));
}
/**
 * What starts the graph. Shown both in Graph settings and on the entry pill,
 * which is where an author looks first — the pill says "Select trigger", so
 * selecting it has to be what offers the choice.
 */
function TriggerPicker({ trigger, availableTriggers, onChange, }) {
    const { fixedEntry } = useContext(FlowMetaContext);
    // Incoming is offered on its own, so it never doubles up here.
    const outboundTriggers = availableTriggers.filter(option => option.eventName !== INCOMING_COMMUNICATION_EVENT_NAME);
    return (_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", htmlFor: "graph-trigger", children: "Select trigger" }), _jsxs("select", { id: "graph-trigger", className: "w-full h-8 rounded-md border bg-background px-2 text-xs disabled:cursor-not-allowed disabled:opacity-50", 
                // The playground runs one way, so its trigger is a fact, not a choice.
                disabled: fixedEntry, value: trigger?.eventName ?? '', onChange: e => {
                    const eventName = e.target.value;
                    if (!eventName) {
                        onChange(null);
                        return;
                    }
                    // The event name is what the platform matches an inbound event
                    // against, so the graph stores it either way. Incoming has no row
                    // to reference; every other trigger carries its id.
                    const picked = availableTriggers.find(option => option.eventName === eventName);
                    onChange({ triggerId: picked?.triggerId ?? null, eventName });
                }, children: [_jsx("option", { value: "", children: "Select trigger" }), _jsx("optgroup", { label: "Inbound", children: _jsx("option", { value: INCOMING_COMMUNICATION_EVENT_NAME, children: "Incoming communication" }) }), _jsx("optgroup", { label: "Outbound", children: outboundTriggers.length > 0 ? (outboundTriggers.map(option => (_jsx("option", { value: option.eventName, children: option.name }, option.id)))) : (
                        // Shown rather than hidden: an absent group reads as a bug, while
                        // an empty one says the company has not made a trigger yet.
                        _jsx("option", { disabled: true, children: "No outbound triggers yet" })) })] })] }));
}
/** The panel's heading, naming what starts the graph underneath it. */
function StartConversationHeading() {
    const { triggerLabel } = useContext(FlowMetaContext);
    return (_jsxs("div", { className: "space-y-0.5", children: [_jsxs("h3", { className: "flex items-center gap-1.5 text-sm font-medium", children: [_jsx(Flag, { className: "h-3.5 w-3.5" }), "Start conversation"] }), _jsx("p", { className: "text-xs text-muted-foreground", children: triggerLabel
                    ? `Trigger: ${triggerLabel}`
                    : 'To configure this node, first select a trigger.' })] }));
}
/**
 * What the Start node offers. Nothing until a trigger is picked: the trigger
 * names the event field holding the destination, so it decides which channels
 * can be reached at all.
 */
function StartConversationFields({ step, onChange, }) {
    const { triggerLabel, triggerChoices, fixedEntry } = useContext(FlowMetaContext);
    // One row only: an outbound graph sends on one channel to one variable, and
    // an inbound one answers on whichever channel the message arrived on.
    const picked = pickedDestinationChoice(triggerChoices, step);
    return (_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", htmlFor: "start-channel", children: "Select eligible channels" }), _jsxs("select", { id: "start-channel", className: "w-full h-8 rounded-md border bg-background px-2 text-xs disabled:cursor-not-allowed disabled:opacity-50", 
                // The trigger decides what can be reached, so there is nothing to
                // offer until one is picked. A fixed entry is not a choice either.
                disabled: !triggerLabel || fixedEntry, value: picked?.id ?? '', onChange: e => {
                    const next = triggerChoices.find(choice => choice.id === e.target.value);
                    onChange({
                        channels: [...(next?.channels ?? [])],
                        destinationVariable: next?.variable ?? null,
                    });
                }, children: [_jsx("option", { value: "", children: "Select channels" }), triggerChoices.map(choice => (_jsx("option", { value: choice.id, 
                        // Kept in the list rather than dropped, so the author reads what
                        // the trigger lacks instead of looking for a missing option.
                        disabled: !!choice.disabledReason, children: choice.disabledReason
                            ? `${choice.label} — ${choice.disabledReason}`
                            : choice.label }, choice.id)))] }), fixedEntry ? (_jsx("p", { className: "text-xs text-muted-foreground", children: "The demo agent answers a call in your browser, so it runs on the phone." })) : picked?.variable ? (_jsxs("p", { className: "text-xs text-muted-foreground", children: [`{${picked.variable}}`, " is defined in the \u201C", triggerLabel, "\u201D trigger"] })) : null] }));
}
function StepPanel({ nodeId, step, knowledgeBases, graphKnowledgeBaseIds, availableScratchpadPaths, variables, nodeActionGroupsById, onChange, onNavigateToAdminAction, renderActionInspector, renderActionInspectorFooter, renderActionPicker, onFetchActionGroupUsage, onDuplicateActionGroup, onRefreshNodeActionGroup, transferActionReferencesById, onFetchTransferActionUsageCount, onDuplicateTransferActionForNode, onSaveTransferActionParameters, onRefreshTransferAction, onTransferActionDirtyChange, onTransferEditingValidateChange, onNavigateToTransferAction, renderTransferEditForm, availableTransferActions, onPickTransferAction, onCreateInlineTransferForNode, describeActionMember, describeTransferRecipient, }) {
    if (step.kind === 'form') {
        return (_jsx("div", { className: "p-4", children: _jsx(FormPanel, { form: {
                    id: nodeId,
                    type: 'form',
                    title: step.title,
                    items: step.items,
                    knowledgeBaseIds: step.knowledgeBaseIds,
                    excludedKnowledgeBaseIds: step.excludedKnowledgeBaseIds,
                    tools: step.tools,
                }, knowledgeBases: knowledgeBases, graphKnowledgeBaseIds: graphKnowledgeBaseIds, availableScratchpadPaths: availableScratchpadPaths, variables: variables, onChange: (patch, options) => onChange(patch, options) }) }));
    }
    if (step.kind === 'end_conversation') {
        return (_jsx("div", { className: "p-4", children: _jsx(EndConversationFields, { step: step, variables: variables, onChange: onChange }) }));
    }
    if (step.kind === 'action') {
        return (_jsx(ActionNodePanel, { actionType: step.actionType, nodeActionGroupId: step.nodeActionGroupId, reference: resolveAsyncRef(nodeActionGroupsById, step.nodeActionGroupId), onNavigateToAdminAction: onNavigateToAdminAction, onReplaceGroupId: newGroupId => onChange({ nodeActionGroupId: newGroupId }), renderInspector: renderActionInspector, renderInspectorFooter: renderActionInspectorFooter, renderActionPicker: renderActionPicker, onFetchUsageCount: onFetchActionGroupUsage, onDuplicateGroup: onDuplicateActionGroup, onRefresh: onRefreshNodeActionGroup, describeMember: describeActionMember }));
    }
    if (step.kind === 'transfer') {
        return (_jsx(TransferNodePanel, { workflowActionId: step.workflowActionId, reference: resolveAsyncRef(transferActionReferencesById, step.workflowActionId), options: availableTransferActions ?? [], onPickExisting: id => onPickTransferAction?.(id), onCreateInline: async (parameters) => onCreateInlineTransferForNode
                ? await onCreateInlineTransferForNode(parameters)
                : null, onFetchUsageCount: async (id) => onFetchTransferActionUsageCount
                ? await onFetchTransferActionUsageCount(id)
                : 1, onDuplicate: async (id, parameters) => onDuplicateTransferActionForNode
                ? await onDuplicateTransferActionForNode(id, parameters)
                : null, onSaveParameters: async (id, parameters) => onSaveTransferActionParameters
                ? await onSaveTransferActionParameters(id, parameters)
                : false, onRefresh: onRefreshTransferAction
                ? async (id) => {
                    await onRefreshTransferAction(id);
                }
                : undefined, onDirtyChange: onTransferActionDirtyChange, onEditingValidateChange: onTransferEditingValidateChange, onNavigateToCatalog: onNavigateToTransferAction, renderEditForm: renderTransferEditForm, describeRecipient: describeTransferRecipient }));
    }
    if (step.kind === 'start_conversation') {
        return (_jsxs("div", { className: "space-y-4 p-4", children: [_jsx(StartConversationHeading, {}), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Title" }), _jsx(Input, { value: step.title, onChange: e => onChange({ title: e.target.value }), placeholder: "Start conversation", className: "text-xs h-8" })] }), _jsx(StartConversationFields, { step: step, onChange: onChange })] }));
    }
    return (_jsxs("div", { className: "space-y-4 p-4", children: [_jsx("h3", { className: "text-sm font-medium", children: step.kind === 'say' ? 'Say node' : 'Conversation node' }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Title" }), _jsx(Input, { value: step.title, onChange: e => onChange({ title: e.target.value }), placeholder: step.kind === 'say' ? 'Greeting' : 'Qualify the caller', className: "text-xs h-8" })] }), step.kind === 'say' ? (_jsx(SayFields, { step: step, variables: variables, onChange: onChange })) : (_jsx(ConversationFields, { step: step, knowledgeBases: knowledgeBases, graphKnowledgeBaseIds: graphKnowledgeBaseIds, availableScratchpadPaths: availableScratchpadPaths, variables: variables, onChange: onChange })), _jsx("div", { className: "h-4", "aria-hidden": true })] }));
}
function EndConversationFields({ step, variables, onChange, }) {
    const Editor = getVariableTextEditor();
    const signOff = step.signOff;
    const mode = signOff?.mode ?? 'exact';
    const promptText = signOff?.promptText ?? '';
    const exactText = signOff?.exactText ?? '';
    const update = (next) => {
        onChange({
            signOff: {
                mode,
                promptText,
                exactText,
                ...next,
            },
        });
    };
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("h3", { className: "text-sm font-medium", children: "End conversation node" }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { className: "text-xs", children: "Optional sign-off message" }), _jsxs(ToggleGroup, { type: "single", variant: "outline", value: mode, onValueChange: value => {
                            if (value === 'prompt' || value === 'exact')
                                update({ mode: value });
                        }, className: "w-full", children: [_jsx(ToggleGroupItem, { value: "prompt", variant: "outline", size: "sm", className: "flex-1", children: "Prompt" }), _jsx(ToggleGroupItem, { value: "exact", variant: "outline", size: "sm", className: "flex-1", children: "Exact message" })] }), mode === 'prompt' ? (_jsxs(_Fragment, { children: [_jsx(Label, { className: "text-xs", children: "Instructions" }), _jsx(Editor, { value: promptText, onValueChange: v => update({ promptText: v }), placeholder: "Provide your assistant with a script to follow, specifying what should be said to the recipient", variables: variables, className: "block w-full min-h-[120px] resize-y border rounded-md px-2 py-1.5", rows: 5 }), _jsx(FieldHelp, { children: "The agent generates the sign-off from these instructions, then hangs up." })] })) : (_jsxs(_Fragment, { children: [_jsx(Label, { className: "text-xs", children: "Exact message" }), _jsx(Editor, { value: exactText, onValueChange: v => update({ exactText: v }), placeholder: "Thanks, have a great day.", variables: variables, className: "block w-full min-h-[120px] resize-y border rounded-md px-2 py-1.5", rows: 5 }), _jsx(FieldHelp, { children: "The agent speaks this line as written, then hangs up." })] }))] })] }));
}
function SayFields({ step, variables, onChange, }) {
    const Editor = getVariableTextEditor();
    return (_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "What the agent says" }), _jsx(Editor, { value: step.text, onValueChange: text => onChange({ text }), placeholder: "Hi, this is Sam from the leasing office\u2026", variables: variables, className: "block w-full min-h-[160px] resize-y border rounded-md px-2 py-1.5", rows: 6 }), _jsx(FieldHelp, { children: "Delivered as written. The call then moves on by itself, so this node needs one line out of it and nothing to configure on it." })] }));
}
function ConversationFields({ step, knowledgeBases, graphKnowledgeBaseIds, availableScratchpadPaths, variables, onChange, }) {
    const Editor = getVariableTextEditor();
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2 cursor-pointer", onClick: () => onChange({ speaksOnEntry: !step.speaksOnEntry }), children: [_jsx("div", { className: cn('flex h-4 w-4 items-center justify-center rounded border-2 transition-colors flex-shrink-0', step.speaksOnEntry
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted-foreground/30'), children: step.speaksOnEntry && _jsx(Check, { className: "h-2.5 w-2.5" }) }), _jsx("span", { className: "text-xs", children: "Speak on entry" })] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Instructions for the agent" }), _jsx(Editor, { value: step.prompt, onValueChange: prompt => onChange({ prompt }), placeholder: "Add a prompt. Example: introduce yourself and ask what the caller needs help with\u2026", variables: variables, className: "block w-full min-h-[180px] resize-y border rounded-md px-2 py-1.5", rows: 8 })] }), _jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Tools" }), _jsx(NodeToolsPicker, { tools: step.tools, onChange: tools => onChange({ tools }) }), _jsx(FieldHelp, { children: "The agent can only use these here. A tool that raises a condition is also what lets the call leave this node." })] }), knowledgeBases.length > 0 && (_jsxs("div", { className: "space-y-1.5", children: [_jsx(Label, { className: "text-xs", children: "Knowledge" }), _jsx(NodeKnowledgePicker, { available: knowledgeBases, fromGraph: graphKnowledgeBaseIds, added: step.knowledgeBaseIds, excluded: step.excludedKnowledgeBaseIds, onChange: onChange }), _jsx(FieldHelp, { children: "What this node reads. The graph's own are on by default; turn one off here and this node alone stops reading it." })] })), _jsx(SignalsPanel, { signals: step.signals, availableScratchpadPaths: availableScratchpadPaths, variables: variables, onChange: (signals, options) => onChange({ signals }, options) })] }));
}
/** Width of the right-hand aside when the editor has room for it. */
const ASIDE_WIDTH = 340;
/** Graph left visible beside the aside on a narrow editor. */
const MIN_GRAPH_PEEK = 80;
export const FlowEditor = forwardRef(function FlowEditor({ initial, onDirty, knowledgeBases = EMPTY_KNOWLEDGE_BASES, availableTriggers = EMPTY_TRIGGERS, rightPanel, activeNodeId = null, previousNodeId = null, colorMode = 'light', collapsibleInspector = false, fixedEntry = false, initialPositions, initialEdgeLabelPositions, layoutDirection = 'LR', embedded: _embedded = false, availableScratchpadPaths = EMPTY_SCRATCHPAD_PATHS, routingOptions, variables, nodeActionGroupsById, onNavigateToAdminAction, describeActionGroup, describeActionMember, describeTransferRecipient, renderActionInspector, renderActionInspectorFooter, renderActionPicker, onFetchActionGroupUsage, onDuplicateActionGroup, onRefreshNodeActionGroup, availableTransferActions, transferActionReferencesById, onCreateInlineTransferAction, onSaveTransferActionParameters, onRefreshTransferAction, onFetchTransferActionUsageCount, onDuplicateTransferActionForNode, onTransferActionDirtyChange, onTransferEditingValidateChange, onNavigateToTransferAction, renderTransferEditForm, }, ref) {
    // Author-chosen direction. The prop is the initial value; Autofit sets a
    // new one at runtime.
    const [effectiveDirection, setEffectiveDirection] = useState(layoutDirection);
    // An empty `initialPositions` object means "no saved layout" (admin passes
    // `{}` for a template with no stored layoutMetadata), not "every node sits
    // at (0,0)". Treat it as absent so ELK runs on first mount.
    const hasInitialPositions = !!initialPositions && Object.keys(initialPositions).length > 0;
    const [nodes, setNodes] = useState(() => buildNodes(initial, hasInitialPositions
        ? new Map(Object.entries(initialPositions))
        : undefined, layoutDirection));
    const [edges, setEdges] = useState(() => buildEdges(initial, initialEdgeLabelPositions, layoutDirection));
    const nodesRef = useRef(nodes);
    // Transition rows added with "+" and not yet connected. A draft can carry
    // its condition before it has a target. Editor-only: not in the canvas,
    // so save skips them. Undo and redo cover them (EditableState).
    const [drafts, setDrafts] = useState([]);
    const [selectedDraftId, setSelectedDraftId] = useState(null);
    nodesRef.current = nodes;
    // Set by <FocusHandle/> inside <ReactFlow>. Lets the imperative handle
    // call setCenter without needing to be inside the ReactFlow context.
    const focusStartRef = useRef(null);
    const focusNodeRef = useRef(null);
    // ELK runs async. applyAutoLayout computes positions for the given
    // canvas in the given direction, then merges them into whatever nodes are
    // in state right now. Nodes already positioned by the caller (manual
    // drag, saved layout) stay put unless `replace` is true. In `pinned`
    // mode, ELK runs with INTERACTIVE strategies and the current node
    // positions as seeds — nodes don't move, only edges re-route and labels
    // re-place to resolve collisions after a structural change.
    const applyAutoLayout = useCallback((canvas, direction, options) => {
        const replace = options?.replace ?? false;
        const pinned = options?.pinned;
        let cancelled = false;
        layoutPositions(canvas, direction, {
            pinnedPositions: pinned,
            sizes: options?.sizes,
            drafts: options?.drafts,
        }).then(result => {
            if (cancelled)
                return;
            setNodes(current => current.map(node => {
                const next = result.nodes.get(node.id);
                if (!next)
                    return node;
                if (!replace &&
                    (node.position.x !== 0 || node.position.y !== 0)) {
                    return node;
                }
                return { ...node, position: next };
            }));
            // ArrowEdge is pure smoothstep now; it does not read any cached
            // ELK bend geometry. Not writing routePoints or routeLabelPos onto
            // edge.data avoids a round of edge re-renders after every layout
            // pass that would otherwise do nothing useful.
        });
        return () => {
            cancelled = true;
        };
    }, []);
    // Initial mount: if the parent passed saved positions, honor them; else
    // run ELK against the starting canvas so the first paint settles into a
    // laid-out grid instead of a stack at origin. Card heights vary with
    // their rows, so ELK waits for measured sizes; a hidden canvas never
    // measures, so it falls back to the constants.
    const initialLayoutDone = useRef(hasInitialPositions);
    const allMeasured = nodes.every(node => !!node.measured?.width && !!node.measured?.height);
    useEffect(() => {
        if (initialLayoutDone.current)
            return;
        const run = () => {
            if (initialLayoutDone.current)
                return;
            initialLayoutDone.current = true;
            applyAutoLayout(initial, layoutDirection, {
                sizes: measuredSizes(nodesRef.current),
            });
        };
        if (allMeasured) {
            run();
            return;
        }
        const handle = window.setTimeout(run, MEASURE_WAIT_MS);
        return () => window.clearTimeout(handle);
        // `initial` and `layoutDirection` are the initial props and should not
        // re-fire this effect on re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMeasured]);
    // Toggle ReactFlow's built-in animated dash on the edge whose source and
    // target match the just-completed transition. Clear after 1.6s so idle
    // edges stop moving.
    useEffect(() => {
        if (!previousNodeId || !activeNodeId || previousNodeId === activeNodeId) {
            return;
        }
        const matching = new Set(edges
            .filter(edge => edge.source === previousNodeId && edge.target === activeNodeId)
            .map(edge => edge.id));
        if (matching.size === 0)
            return;
        setEdges(prev => prev.map(edge => matching.has(edge.id) ? { ...edge, animated: true } : edge));
        const handle = window.setTimeout(() => {
            setEdges(prev => prev.map(edge => matching.has(edge.id) ? { ...edge, animated: false } : edge));
        }, 1600);
        return () => window.clearTimeout(handle);
        // buildEdges only rebuilds on template change (via `key`), so we can
        // safely depend on the fingerprints alone here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousNodeId, activeNodeId]);
    const [persona, setPersona] = useState(initial.persona);
    const [knowledgeBaseIds, setKnowledgeBaseIds] = useState(initial.knowledgeBaseIds);
    const [watchEvents, setWatchEvents] = useState(initial.watchEvents);
    const [trigger, setTrigger] = useState(initial.trigger);
    const triggerReach = useMemo(() => triggerReachOf(trigger, availableTriggers), [trigger, availableTriggers]);
    const [voice, setVoice] = useState(initial.voice);
    const [history, setHistory] = useState(() => historyStack.empty());
    // The field typed into last, e.g. `draft:<id>:label`. Typing joins the top
    // undo step only while it stays the same; any other undo step clears it.
    const typingKey = useRef(null);
    const steps = useMemo(() => nodes
        .filter(n => n.type === 'step' || n.type === 'form')
        .map(n => stepOf(n.id, n.data)), [nodes]);
    const stepById = useMemo(() => new Map(steps.map(step => [step.id, step])), [steps]);
    // List order is priority: the node data carries it, so undo covers it.
    const flowStarts = useMemo(() => nodes
        .filter(n => n.type === 'flowStart')
        .map(n => ({ id: n.id, data: n.data }))
        .sort((a, b) => a.data.order - b.data.order)
        .map(n => ({ id: flowIdOf(n.id), events: n.data.events })), [nodes]);
    // The canvas as one value, for saving, autofit and the arrow panel.
    const canvas = useMemo(() => ({
        persona,
        ...(voice ? { voice } : {}),
        trigger,
        knowledgeBaseIds,
        watchEvents,
        steps,
        flowStarts,
        // Every edge, the entry one included: it carries the id the API
        // stores, so leaving it out would make each save delete and remake it.
        arrows: edges.map(arrowOf),
    }), [persona, voice, knowledgeBaseIds, watchEvents, steps, flowStarts, edges]);
    const startTarget = edges.find(e => e.source === TRIGGER_NODE_ID)?.target ?? null;
    const stepArrows = useMemo(() => edges.filter(e => e.source !== TRIGGER_NODE_ID), [edges]);
    const channelsReachingNode = useMemo(() => {
        const byNode = new Map();
        for (const start of steps) {
            if (start.type !== 'start_conversation')
                continue;
            const queue = [start.id];
            const seen = new Set([start.id]);
            while (queue.length > 0) {
                const current = queue.shift();
                const held = byNode.get(current);
                byNode.set(current, held
                    ? held.filter(channel => start.channels.includes(channel))
                    : start.channels);
                for (const edge of stepArrows) {
                    if (edge.source !== current || edge.target === END_NODE_ID)
                        continue;
                    if (seen.has(edge.target))
                        continue;
                    seen.add(edge.target);
                    queue.push(edge.target);
                }
            }
        }
        return byNode;
    }, [steps, stepArrows]);
    // Say and end-conversation nodes own unconditional edges; their card
    // shows one fixed "Unconditional" row.
    const implicitSources = useMemo(() => new Set(steps
        .filter(step => isImplicitSource(step.id, stepById))
        .map(step => step.id)), [steps, stepById]);
    // Slot lists ordered by the geometric position of what each slot
    // connects to — not insertion order. For source slots: order by the
    // target node's cross-axis coordinate (y in LR, x in TB), so the
    // top-most outgoing edge leaves the top-most slot and reaches its
    // target without climbing over siblings. Same inversion for target
    // slots. Minimizes edge crossings without re-running a full layout.
    const nodePositionById = useMemo(() => {
        const map = new Map();
        for (const node of nodes)
            map.set(node.id, node.position);
        return map;
    }, [nodes]);
    const crossAxisOf = (nodeId) => {
        const pos = nodePositionById.get(nodeId);
        if (!pos)
            return 0;
        return effectiveDirection === 'LR' ? pos.y : pos.x;
    };
    const sortedSlots = (entries) => entries
        .sort((a, b) => a.axis - b.axis || a.order - b.order)
        .map(e => e.slot);
    const sourceSlotsByNode = useMemo(() => {
        const perNode = new Map();
        const seen = new Map();
        edges.forEach((edge, order) => {
            const slot = sourceSlotFor({
                source: edge.source,
                target: edge.target,
                side: sourceSideFor(edge.source, stepById, edge.data?.sourceSide ??
                    defaultSourceSide(effectiveDirection)),
            });
            const slotsSeen = seen.get(edge.source) ?? new Set();
            if (slotsSeen.has(slot))
                return;
            slotsSeen.add(slot);
            seen.set(edge.source, slotsSeen);
            const list = perNode.get(edge.source) ?? [];
            list.push({
                slot,
                axis: crossAxisOf(edge.target),
                order,
            });
            perNode.set(edge.source, list);
        });
        const map = new Map();
        for (const [nodeId, entries] of perNode) {
            map.set(nodeId, sortedSlots(entries));
        }
        return map;
    }, [edges, nodePositionById, effectiveDirection, stepById]);
    // Nodes on either end of any currently-selected edge — outlined in green
    // so the operator can see which pair the selected arrow connects.
    const edgeHighlightedNodeIds = useMemo(() => {
        const set = new Set();
        for (const edge of edges) {
            if (!edge.selected)
                continue;
            set.add(edge.source);
            set.add(edge.target);
        }
        return set;
    }, [edges]);
    // The actions of each group on the canvas, as an arrow names them.
    const actionChoices = useMemo(() => {
        const byGroup = {};
        for (const [id, group] of Object.entries(nodeActionGroupsById ?? {})) {
            if (!group)
                continue;
            // Only an action that runs after the call reports a result.
            const members = group.members.filter(member => POST_CALL_ACTION_TYPES.has(member.workflowAction.actionType));
            const labels = members.map(member => memberLabel(member, describeActionMember));
            // Two actions that read the same take their place in the group.
            byGroup[id] = members.map((member, i) => ({
                value: member.workflowActionId,
                label: labels.indexOf(labels[i]) === labels.lastIndexOf(labels[i])
                    ? labels[i]
                    : `${i + 1}. ${labels[i]}`,
            }));
        }
        return byGroup;
    }, [nodeActionGroupsById, describeActionMember]);
    // What a transition row says: its name, or its condition in words.
    const arrowLabel = useMemo(() => {
        const actionNames = new Map(Object.values(actionChoices)
            .flat()
            .map(choice => [choice.value, choice.label]));
        return (data) => data?.label?.trim() ||
            summarize(data?.condition, valueLabelFrom(routingOptions), id => actionNames.get(id));
    }, [routingOptions, actionChoices]);
    const edgeLabels = useMemo(() => {
        const map = new Map();
        for (const e of edges) {
            map.set(e.id, arrowLabel(e.data));
        }
        return map;
    }, [edges, arrowLabel]);
    const draftsBySource = useMemo(() => {
        const map = new Map();
        for (const draft of drafts) {
            map.set(draft.source, [
                ...(map.get(draft.source) ?? []),
                {
                    id: draft.id,
                    label: arrowLabel(draft),
                    selected: draft.id === selectedDraftId,
                    at: draft.at,
                },
            ]);
        }
        return map;
    }, [drafts, arrowLabel, selectedDraftId]);
    // A node's arrows and drafts in the order its card lists them.
    const rowsOf = (source) => listOrder(edges.filter(edge => edge.source === source), drafts.filter(draft => draft.source === source));
    // The rows each card lists, in the order the arrows are checked.
    const transitionsBySource = useMemo(() => {
        const map = new Map();
        for (const edge of edges) {
            if (!stepById.has(edge.source))
                continue;
            const rows = map.get(edge.source) ?? [];
            rows.push({
                id: edge.id,
                target: edge.target,
                selected: !!edge.selected,
            });
            map.set(edge.source, rows);
        }
        return map;
    }, [edges, stepById]);
    const targetSlotsByNode = useMemo(() => {
        const perNode = new Map();
        const seen = new Map();
        edges.forEach((edge, order) => {
            const slot = targetSlotFor({
                source: edge.source,
                target: edge.target,
                side: resolveTargetSide(edge.data, effectiveDirection),
            });
            const slotsSeen = seen.get(edge.target) ?? new Set();
            if (slotsSeen.has(slot))
                return;
            slotsSeen.add(slot);
            seen.set(edge.target, slotsSeen);
            const list = perNode.get(edge.target) ?? [];
            list.push({
                slot,
                axis: crossAxisOf(edge.source),
                order,
            });
            perNode.set(edge.target, list);
        });
        const map = new Map();
        for (const [nodeId, entries] of perNode) {
            map.set(nodeId, sortedSlots(entries));
        }
        return map;
    }, [edges, nodePositionById, effectiveDirection]);
    const reachable = useMemo(() => {
        const roots = [
            startTarget,
            ...stepArrows
                .filter(edge => isFlowStartNodeId(edge.source))
                .map(edge => edge.target),
        ].filter((id) => !!id && id !== END_NODE_ID);
        const set = new Set(roots);
        const queue = [...set];
        while (queue.length > 0) {
            const current = queue.shift();
            for (const edge of stepArrows) {
                if (edge.source !== current || edge.target === END_NODE_ID)
                    continue;
                if (!set.has(edge.target)) {
                    set.add(edge.target);
                    queue.push(edge.target);
                }
            }
        }
        return set;
    }, [startTarget, stepArrows]);
    useImperativeHandle(ref, () => ({
        getPositions: () => Object.fromEntries(nodes.map(node => [node.id, { ...node.position }])),
        getLayoutMetadata: () => ({
            // Every node position persists — Start and End included. Filtering
            // them out silently caused a reopen to lose their positions and
            // land them at (0,0), stacking Start/End over the first step.
            nodes: Object.fromEntries(nodes.map(node => [node.id, { ...node.position }])),
            edges: Object.fromEntries(edges.map(edge => {
                const data = edge.data;
                return [edge.id, data?.bend ?? null];
            })),
        }),
        getState: () => canvas,
        focusStart: () => {
            focusStartRef.current?.();
        },
        focusFirstStep: () => {
            const firstId = startTarget ?? steps[0]?.id ?? null;
            if (!firstId)
                return;
            setNodes(ns => ns.map(n => ({ ...n, selected: n.id === firstId })));
            setEdges(es => es.map(e => ({ ...e, selected: false })));
            focusNodeRef.current?.(firstId);
        },
        selectFirstStep: () => {
            const firstId = startTarget ?? steps[0]?.id ?? null;
            if (!firstId)
                return null;
            setNodes(ns => ns.map(n => ({ ...n, selected: n.id === firstId })));
            setEdges(es => es.map(e => ({ ...e, selected: false })));
            return firstId;
        },
        openInspector: () => setPanelCollapsed(false),
        clearSelection: () => {
            setNodes(ns => ns.map(n => ({ ...n, selected: false })));
            setEdges(es => es.map(e => ({ ...e, selected: false })));
        },
        focusNode: id => {
            const exists = nodes.some(n => n.id === id);
            if (!exists)
                return false;
            setNodes(ns => ns.map(n => ({ ...n, selected: n.id === id })));
            setEdges(es => es.map(e => ({ ...e, selected: false })));
            focusNodeRef.current?.(id);
            setPanelCollapsed(false);
            setInspectorOpen(true);
            setRightTab('node');
            return true;
        },
        focusEdge: id => {
            const exists = edges.some(e => e.id === id);
            if (!exists)
                return false;
            setEdges(es => es.map(e => ({ ...e, selected: e.id === id })));
            setNodes(ns => ns.map(n => ({ ...n, selected: false })));
            setPanelCollapsed(false);
            return true;
        },
        getSelectedStepId: () => {
            const match = nodes.find(n => n.selected && (n.type === 'step' || n.type === 'form'));
            return match ? match.id : null;
        },
        loadCanvas: (canvas, options) => {
            const push = options?.pushHistory ?? true;
            if (push) {
                typingKey.current = null;
                setHistory(current => historyStack.push(current, {
                    nodes,
                    edges,
                    drafts,
                    persona,
                    knowledgeBaseIds,
                    trigger,
                }));
            }
            const positionMap = options?.positions
                ? new Map(Object.entries(options.positions))
                : undefined;
            // Use live direction, not the initial prop — mixing them drops edges after Autofit.
            setNodes(buildNodes(canvas, positionMap, effectiveDirection));
            setEdges(buildEdges(canvas, undefined, effectiveDirection));
            if (!positionMap) {
                // Ids the canvas shares with the current one keep their measured
                // size; the rest use the constants.
                applyAutoLayout(canvas, effectiveDirection, {
                    replace: true,
                    sizes: measuredSizes(nodes),
                });
            }
            setPersona(canvas.persona);
            setKnowledgeBaseIds(canvas.knowledgeBaseIds);
            setWatchEvents(canvas.watchEvents);
            setVoice(canvas.voice);
            setDrafts([]);
        },
    }), [
        persona,
        voice,
        knowledgeBaseIds,
        watchEvents,
        startTarget,
        steps,
        flowStarts,
        nodes,
        edges,
        drafts,
        layoutDirection,
    ]);
    const handleAutofit = useCallback((direction) => {
        typingKey.current = null;
        setHistory(current => historyStack.push(current, {
            nodes,
            edges,
            drafts,
            persona,
            knowledgeBaseIds,
            trigger,
        }));
        setEffectiveDirection(direction);
        const sizes = measuredSizes(nodes);
        setNodes(buildNodes(canvas, undefined, direction));
        applyAutoLayout(canvas, direction, { replace: true, sizes, drafts });
        // Autofit resets every edge to the new direction's default sides.
        // Without this, edges carry their old sourceHandle (e.g. `src-right`
        // from LR) which does not exist as a slot handle after switching to
        // TB — React Flow logs #008 and drops the edge visually.
        const nextSourceSide = defaultSourceSide(direction);
        const nextTargetSide = defaultTargetSide(direction);
        setEdges(current => current.map(edge => {
            const prev = edge.data;
            const sourceSide = sourceSideFor(edge.source, stepById, nextSourceSide);
            const { bend: _dropped, ...rest } = {
                ...prev,
                sourceSide,
                targetSide: nextTargetSide,
            };
            const nextData = rest;
            return {
                ...edge,
                data: nextData,
                sourceHandle: sourceSlotFor({
                    source: edge.source,
                    target: edge.target,
                    side: sourceSide,
                }),
                targetHandle: targetSlotFor({
                    source: edge.source,
                    target: edge.target,
                    side: nextTargetSide,
                }),
            };
        }));
        onDirty?.();
    }, [
        nodes,
        edges,
        persona,
        voice,
        knowledgeBaseIds,
        watchEvents,
        steps,
        stepById,
        flowStarts,
        drafts,
        onDirty,
    ]);
    const snapshot = useCallback(() => {
        typingKey.current = null;
        setHistory(current => historyStack.push(current, {
            nodes,
            edges,
            drafts,
            persona,
            knowledgeBaseIds,
            trigger,
        }));
    }, [nodes, edges, drafts, persona, knowledgeBaseIds, trigger]);
    const apply = useCallback((state) => {
        typingKey.current = null;
        setNodes(state.nodes);
        setEdges(state.edges);
        setDrafts(state.drafts);
        setPersona(state.persona);
        setKnowledgeBaseIds(state.knowledgeBaseIds);
        setTrigger(state.trigger);
    }, []);
    const doUndo = useCallback(() => {
        const result = historyStack.undo(history, {
            nodes,
            edges,
            drafts,
            persona,
            knowledgeBaseIds,
            trigger,
        });
        if (!result)
            return;
        setHistory(result.history);
        apply(result.snapshot);
        onDirty();
    }, [
        history,
        nodes,
        edges,
        drafts,
        persona,
        knowledgeBaseIds,
        trigger,
        apply,
        onDirty,
    ]);
    const doRedo = useCallback(() => {
        const result = historyStack.redo(history, {
            nodes,
            edges,
            drafts,
            persona,
            knowledgeBaseIds,
            trigger,
        });
        if (!result)
            return;
        setHistory(result.history);
        apply(result.snapshot);
        onDirty();
    }, [
        history,
        nodes,
        edges,
        drafts,
        persona,
        knowledgeBaseIds,
        trigger,
        apply,
        onDirty,
    ]);
    const onNodesChange = useCallback((changes) => {
        // Position snapshots happen in onNodeDragStart, where the closure
        // still holds the pre-drag state; snapshotting here would capture
        // the last mid-drag frame and make undo look like a no-op.
        if (changes.some(change => change.type === 'remove')) {
            snapshot();
        }
        setNodes(ns => applyNodeChanges(changes, ns));
        if (changes.some(change => change.type === 'remove'))
            onDirty();
    }, [onDirty, snapshot]);
    const onNodeDragStart = useCallback(() => {
        snapshot();
    }, [snapshot]);
    const onNodeDragStop = useCallback(() => {
        onDirty();
    }, [onDirty]);
    const onEdgesChange = useCallback((changes) => {
        if (changes.some(change => change.type === 'remove'))
            snapshot();
        setEdges(es => applyEdgeChanges(changes, es));
        if (changes.some(change => change.type === 'remove'))
            onDirty();
    }, [onDirty, snapshot]);
    // Greys out the drop before the operator releases, so a drag off the
    // trigger shows that Start is its only target.
    const isValidConnection = useCallback((connection) => {
        const { source, target } = connection;
        if (!source || !target)
            return false;
        if (source !== TRIGGER_NODE_ID)
            return true;
        const startId = startConversationIdOf(nodes);
        return !startId || target === startId;
    }, [nodes]);
    const onConnect = useCallback((connection) => {
        const { source, target } = connection;
        if (!source || !target)
            return;
        if (isEntryPill(target) || source === END_NODE_ID)
            return;
        if (source === target)
            return;
        if (source === TRIGGER_NODE_ID && target === END_NODE_ID)
            return;
        // The trigger opens the conversation on the Start node. Only a graph
        // that has lost its Start accepts another target, and the validator
        // reports that graph already.
        if (source === TRIGGER_NODE_ID) {
            const startId = startConversationIdOf(nodes);
            if (startId && target !== startId)
                return;
        }
        // A draft row becomes the arrow it was dragged into and brings its
        // condition along. Drawing to a node that already has an arrow from
        // here selects that one instead and keeps the draft.
        const draft = connection.sourceHandle?.startsWith(DRAFT_HANDLE_PREFIX)
            ? drafts.find(d => d.id ===
                connection.sourceHandle.slice(DRAFT_HANDLE_PREFIX.length))
            : undefined;
        const pairTaken = edges.some(e => e.source === source && e.target === target);
        // The arrow takes the draft's place in the list.
        const settled = draft && !pairTaken
            ? settleRows(drafts, source, rowsOf(source).map(row => row.id === draft.id ? { ...row, draft: false } : row))
            : null;
        if (settled)
            setDrafts(settled.drafts);
        // Cycles allowed — the per-turn navigator treats loops as a routing primitive.
        snapshot();
        const dropSourceSide = sourceSideFor(source, stepById, parseSourceSideFromHandle(connection.sourceHandle, defaultSourceSide(effectiveDirection)));
        const dropTargetSide = parseTargetSideFromHandle(connection.targetHandle, defaultTargetSide(effectiveDirection));
        if (isEntryPill(source)) {
            // One edge out of Start and out of each cross-cutting flow.
            // Re-pointing keeps the edge's id, so the API updates the row
            // rather than swapping it for a new one.
            const flow = isFlowStartNodeId(source);
            setEdges(es => {
                const entry = es.find(e => e.source === source);
                const id = flow ? flowIdOf(source) : (entry?.id ?? newId());
                const data = {
                    sourceSide: dropSourceSide,
                    targetSide: dropTargetSide,
                };
                return [
                    ...es.filter(e => e.source !== source),
                    {
                        id,
                        source,
                        target,
                        sourceHandle: sourceSlotFor({
                            source,
                            target,
                            side: dropSourceSide,
                        }),
                        targetHandle: targetSlotFor({
                            source,
                            target,
                            side: dropTargetSide,
                        }),
                        type: 'arrow',
                        animated: true,
                        data,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    },
                ];
            });
        }
        else {
            // One arrow between two nodes: drawing it again selects the one
            // there, which takes the sides of the new drop. A new arrow starts
            // with no condition; the operator sets it in the panel.
            const newSourceHandle = sourceSlotFor({
                source,
                target,
                side: dropSourceSide,
            });
            const newTargetHandle = targetSlotFor({
                source,
                target,
                side: dropTargetSide,
            });
            setEdges(es => {
                const existing = es.find(e => e.source === source && e.target === target);
                const placed = (data) => ({
                    ...data,
                    sourceSide: dropSourceSide,
                    targetSide: dropTargetSide,
                });
                const unselected = es.map(e => e.selected ? { ...e, selected: false } : e);
                if (existing) {
                    return unselected.map(e => e.id === existing.id
                        ? {
                            ...e,
                            sourceHandle: newSourceHandle,
                            targetHandle: newTargetHandle,
                            data: placed(e.data),
                            selected: true,
                        }
                        : e);
                }
                const added = [
                    ...unselected,
                    {
                        id: draft?.id ?? newId(),
                        source,
                        target,
                        sourceHandle: newSourceHandle,
                        targetHandle: newTargetHandle,
                        type: 'arrow',
                        data: placed(draft
                            ? {
                                ...(draft.condition
                                    ? { condition: draft.condition }
                                    : {}),
                                ...(draft.label ? { label: draft.label } : {}),
                            }
                            : undefined),
                        selected: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    },
                ];
                return settled ? orderSiblings(added, source, settled.order) : added;
            });
            setNodes(ns => ns.map(n => (n.selected ? { ...n, selected: false } : n)));
            setPanelCollapsed(false);
            setInspectorOpen(true);
            setRightTab('node');
        }
        onDirty();
    }, [onDirty, snapshot, stepById, effectiveDirection, drafts, edges]);
    const nextStepPosition = () => ({
        x: Math.max(200, ...nodes
            .filter(n => n.type === 'step' || n.type === 'form')
            .map(n => n.position.x)) + 60,
        y: 240,
    });
    const insertStep = (data, isForm) => {
        const id = newId();
        setNodes(ns => [
            ...ns.map(n => ({ ...n, selected: false })),
            {
                id,
                type: isForm ? 'form' : 'step',
                position: nextStepPosition(),
                data: data,
                selected: true,
            },
        ]);
        setEdges(es => es.map(e => ({ ...e, selected: false })));
        revealNodeTab();
        onDirty();
        requestAnimationFrame(() => focusNodeRef.current?.(id));
    };
    const addStep = (kind) => {
        snapshot();
        const data = kind === 'say'
            ? { kind: 'say', title: 'New line', text: '' }
            : kind === 'form'
                ? {
                    kind: 'form',
                    title: 'New form',
                    items: [],
                    knowledgeBaseIds: [],
                    excludedKnowledgeBaseIds: [],
                    tools: [],
                }
                : kind === 'end_conversation'
                    ? { kind: 'end_conversation', title: 'End', signOff: null }
                    : kind === 'transfer'
                        ? // Drops with an empty workflowActionId; the inspector's
                            // inline picker handles pick/create. Save is allowed with
                            // an empty reference so the author can wire outcomes
                            // first; the API rejects on save with an issue the editor
                            // surfaces as a focused error.
                            { kind: 'transfer', title: 'Transfer', workflowActionId: '' }
                        : kind === 'action'
                            ? // Same pattern for action nodes: empty nodeActionGroupId,
                                // picker handles pick/create. Default actionType is EMAIL;
                                // the picker swaps it when the author attaches a group.
                                {
                                    kind: 'action',
                                    title: 'Action',
                                    actionType: ActionType.EMAIL,
                                    nodeActionGroupId: '',
                                }
                            : {
                                kind: 'conversation',
                                title: 'New node',
                                prompt: '',
                                knowledgeBaseIds: [],
                                excludedKnowledgeBaseIds: [],
                                tools: [],
                                signals: [],
                            };
        insertStep(data, kind === 'form');
    };
    /** The inspector's inline picker calls this to write the picked
     *  workflowActionId back onto the current Transfer node. Passing an
     *  empty string clears the reference — the picker reappears. */
    const applyTransferPick = (nodeId, workflowActionId) => {
        snapshot();
        setNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, workflowActionId } } : n));
        onDirty();
    };
    /** The inspector's inline-create path. Delegates to the consumer, then
     *  writes the returned id onto the current Transfer node. */
    const applyTransferCreate = async (nodeId, parameters) => {
        if (!onCreateInlineTransferAction)
            return null;
        const workflowActionId = await onCreateInlineTransferAction(parameters);
        if (!workflowActionId)
            return null;
        applyTransferPick(nodeId, workflowActionId);
        return workflowActionId;
    };
    const selectedNodeIds = nodes
        .filter(n => n.selected && isSelectableNodeType(n.type))
        .map(n => n.id);
    const selectedEdgeIds = edges.filter(e => e.selected).map(e => e.id);
    // A draft is selected only while no node or arrow is.
    const selectedDraft = selectedNodeIds.length === 0 && selectedEdgeIds.length === 0
        ? drafts.find(d => d.id === selectedDraftId)
        : undefined;
    useEffect(() => {
        if (selectedNodeIds.length > 0 || selectedEdgeIds.length > 0) {
            setSelectedDraftId(null);
        }
    }, [selectedNodeIds.length, selectedEdgeIds.length]);
    // Deleting a line keeps its condition as a draft in the arrow's place,
    // unless its node is gone or has no condition to keep. The draft keeps the
    // arrow's id, so reconnecting updates the stored row.
    const keepAsDrafts = (cut, removedNodeIds) => {
        const kept = cut
            .filter(e => !removedNodeIds.has(e.source) &&
            stepById.has(e.source) &&
            !isImplicitSource(e.source, stepById))
            .map(e => {
            const data = e.data;
            return {
                id: e.id,
                source: e.source,
                ...(data?.condition ? { condition: data.condition } : {}),
                ...(data?.label ? { label: data.label } : {}),
            };
        });
        if (kept.length === 0)
            return;
        // Each cut arrow becomes a draft in the arrow's place in the list.
        const cutIds = new Set(cut.map(e => e.id));
        let next = drafts;
        for (const source of new Set(kept.map(d => d.source))) {
            next = settleRows(next, source, rowsOf(source).map(row => cutIds.has(row.id) ? { ...row, draft: true } : row), kept.filter(d => d.source === source)).drafts;
        }
        setDrafts(next);
    };
    const deleteArrow = (edgeId) => {
        const source = edges.find(e => e.id === edgeId)?.source;
        // The trigger's arrow is the graph's only way in.
        if (source === TRIGGER_NODE_ID)
            return;
        snapshot();
        setEdges(es => es.filter(e => e.id !== edgeId));
        // The drafts below it keep their place in the list.
        if (source) {
            setDrafts(settleRows(drafts, source, rowsOf(source).filter(row => row.id !== edgeId)).drafts);
        }
        onDirty();
    };
    const hasSelection = selectedNodeIds.length > 0 ||
        selectedEdgeIds.length > 0 ||
        !!selectedDraft;
    const deleteSelected = () => {
        if (!hasSelection)
            return;
        if (selectedDraft) {
            snapshot();
            setDrafts(ds => ds.filter(d => d.id !== selectedDraft.id));
            return;
        }
        snapshot();
        // React Flow's `deletable` covers selection and the keyboard; this
        // button removes by id, so it honours the same flag itself.
        const locked = new Set(nodes.filter(n => n.deletable === false).map(n => n.id));
        const removed = new Set(selectedNodeIds.filter(id => !locked.has(id)));
        keepAsDrafts(edges.filter(e => (selectedEdgeIds.includes(e.id) && e.deletable !== false) ||
            removed.has(e.source) ||
            removed.has(e.target)), removed);
        setNodes(ns => renumberFlows(ns.filter(n => !removed.has(n.id))));
        setEdges(es => es.filter(e => !(selectedEdgeIds.includes(e.id) && e.deletable !== false) &&
            !removed.has(e.source) &&
            !removed.has(e.target)));
        onDirty();
    };
    const updateStep = (id, patch, options) => {
        if (options?.skipHistory) {
            // Clear redo — any pending future entry was snapshotted BEFORE this
            // out-of-band write (e.g. inference filling metadata on an item the
            // author just undid). Redoing to that pre-write state would show
            // the user stale text without the metadata we just wrote.
            if (history.future.length > 0) {
                setHistory(h => ({ past: h.past, future: [] }));
            }
        }
        else {
            const key = `step:${id}:${Object.keys(patch).join()}`;
            if (typingKey.current !== key ||
                !isPureTextCoalesce(nodes, id, patch, history)) {
                snapshot();
            }
            typingKey.current = key;
        }
        setNodes(ns => ns.map(n => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
        onDirty();
    };
    // Cross-cutting flows: list order in node data is the priority.
    const updateFlowStart = (nodeId, events) => {
        snapshot();
        setNodes(ns => ns.map(n => n.id === nodeId ? { ...n, data: { ...n.data, events } } : n));
        onDirty();
    };
    const addFlowStart = () => {
        snapshot();
        const nodeId = flowStartNodeId(newId());
        // Flows stack beside Start, away from the main graph.
        const start = nodes.find(n => n.id === TRIGGER_NODE_ID)?.position ?? {
            x: 0,
            y: 0,
        };
        setNodes(ns => [
            ...ns.map(n => ({ ...n, selected: false })),
            {
                id: nodeId,
                type: 'flowStart',
                position: {
                    x: start.x - 320,
                    y: start.y + flowStarts.length * 64,
                },
                data: {
                    events: [],
                    order: flowStarts.length,
                },
                selected: true,
            },
        ]);
        setEdges(es => es.map(e => ({ ...e, selected: false })));
        revealNodeTab();
        focusNodeRef.current?.(nodeId);
        onDirty();
    };
    const removeFlowStart = (nodeId) => {
        snapshot();
        setNodes(ns => renumberFlows(ns.filter(n => n.id !== nodeId)));
        setEdges(es => es.filter(e => e.source !== nodeId));
        onDirty();
    };
    const moveFlowStart = (nodeId, offset) => {
        const ids = flowStarts.map(flow => flowStartNodeId(flow.id));
        const from = ids.indexOf(nodeId);
        const to = from + offset;
        if (from < 0 || to < 0 || to >= ids.length)
            return;
        snapshot();
        [ids[from], ids[to]] = [ids[to], ids[from]];
        setNodes(ns => ns.map(n => ids.includes(n.id)
            ? { ...n, data: { ...n.data, order: ids.indexOf(n.id) } }
            : n));
        onDirty();
    };
    const focusFlowStart = (nodeId) => {
        setNodes(ns => ns.map(n => ({ ...n, selected: n.id === nodeId })));
        setEdges(es => es.map(e => ({ ...e, selected: false })));
        revealNodeTab();
        focusNodeRef.current?.(nodeId);
    };
    const updateArrow = (id, patch) => {
        snapshot();
        setEdges(es => es.map(e => {
            if (e.id !== id)
                return e;
            const nextData = { ...e.data, ...patch };
            // sourceHandle is derived from the edge id and does not change
            // when the condition changes — the handle stays put while the
            // data changes. The slot's sort position may shift (if the
            // target position changes), handled by the sourceSlotsByNode
            // useMemo in a separate render pass.
            return { ...e, data: nextData };
        }));
        onDirty();
    };
    useEffect(() => {
        const onKeyDown = (event) => {
            const modifier = event.metaKey || event.ctrlKey;
            if (!modifier || event.key.toLowerCase() !== 'z')
                return;
            const target = event.target;
            // Let native text-input undo run when the caret sits inside a field.
            if (target &&
                target.closest('input, textarea, [contenteditable="true"]')) {
                return;
            }
            event.preventDefault();
            if (event.shiftKey)
                doRedo();
            else
                doUndo();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [doUndo, doRedo]);
    // A draft is not a React Flow element, so deleteKeyCode misses it.
    useEffect(() => {
        if (!selectedDraft)
            return;
        const onKeyDown = (event) => {
            if (event.key !== 'Delete' && event.key !== 'Backspace')
                return;
            const target = event.target;
            if (target &&
                target.closest('input, textarea, [contenteditable="true"]')) {
                return;
            }
            event.preventDefault();
            snapshot();
            setDrafts(ds => ds.filter(d => d.id !== selectedDraft.id));
            setSelectedDraftId(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [selectedDraft, snapshot]);
    const selectedStep = steps.find(s => selectedNodeIds.includes(s.id));
    const selectedEdge = !selectedStep
        ? edges.find(e => selectedEdgeIds.includes(e.id))
        : undefined;
    // One object per selected edge, so the arrow panel's memo holds across renders.
    const selectedArrow = useMemo(() => (selectedEdge ? arrowOf(selectedEdge) : undefined), [selectedEdge]);
    // A flow's arrow opens the flow itself: the arrow has no condition of its own.
    // The entry pill carries the trigger, which belongs to the graph rather
    // than to any node, so it gets its own panel.
    const entrySelected = nodes.some(n => n.id === TRIGGER_NODE_ID && n.selected);
    const selectedFlowNodeId = selectedNodeIds.find(isFlowStartNodeId) ??
        (selectedEdge && isFlowStartNodeId(selectedEdge.source)
            ? selectedEdge.source
            : undefined);
    const flowTargetName = (nodeId) => {
        const target = edges.find(e => e.source === nodeId)?.target;
        if (!target)
            return null;
        if (target === END_NODE_ID)
            return 'End call';
        return stepById.get(target)?.title || target;
    };
    const flowRows = flowStarts.map(flow => {
        const nodeId = flowStartNodeId(flow.id);
        return { nodeId, events: flow.events, target: flowTargetName(nodeId) };
    });
    // User-driven collapse. Separate from the auto-collapse policy
    // (collapsibleInspector) so hiding does not require deselecting.
    const [panelCollapsed, setPanelCollapsed] = useState(false);
    const [rightTab, setRightTab] = useState('graph');
    // A boolean, not the element: callers pass a fresh `rightPanel` element
    // on every render, which would re-run the effects below mid-call.
    const callActive = !!rightPanel;
    // Author intent, driven by release events only. React Flow selects a
    // node on pointer-down, so basing the aside on hasSelection would make
    // it slide in the instant a drag starts and steal the gesture on touch.
    const [inspectorOpen, setInspectorOpen] = useState(false);
    useEffect(() => {
        if (!hasSelection)
            setInspectorOpen(false);
    }, [hasSelection]);
    useEffect(() => {
        // A call opens the aside on its transcript, even if it was hidden.
        if (!callActive)
            return;
        setPanelCollapsed(false);
        setRightTab('test');
    }, [callActive]);
    // The aside stays visible unless the author collapses it. With
    // `collapsibleInspector` on and nothing to show, the aside hides itself.
    const autoVisible = collapsibleInspector
        ? callActive || inspectorOpen
        : true;
    const asideVisible = autoVisible && !panelCollapsed;
    // Tapping a node or a line is a request to edit it: reopen a collapsed
    // aside and land on its details, whatever tab was open before. Click
    // rather than selection, so re-tapping the already-selected node works.
    // During a call the Test tab holds the live transcript, so a tap only
    // reopens the aside and leaves the tab in place.
    const revealNodeTab = () => {
        setPanelCollapsed(false);
        setInspectorOpen(true);
        if (!callActive)
            setRightTab('node');
    };
    // A draft is selected alone, as an arrow is.
    const selectDraft = (draftId) => {
        setNodes(ns => ns.map(n => (n.selected ? { ...n, selected: false } : n)));
        setEdges(es => es.map(e => (e.selected ? { ...e, selected: false } : e)));
        setSelectedDraftId(draftId);
        revealNodeTab();
    };
    // A condition change is one undo step, as on an arrow. Typing a draft's
    // name lands as one step, the way node text does (isPureTextCoalesce).
    const updateDraft = (draftId, patch) => {
        const key = `draft:${draftId}:${Object.keys(patch).join()}`;
        if (typingKey.current !== key ||
            !isDraftLabelCoalesce(drafts, draftId, patch, history)) {
            snapshot();
        }
        typingKey.current = key;
        setDrafts(ds => ds.map(d => (d.id === draftId ? { ...d, ...patch } : d)));
    };
    // Moves a row, arrow or draft, to place `to` in its node's list. Only a
    // change in the arrows' order changes the saved graph.
    const moveListRow = (source, rowId, to) => {
        const rows = rowsOf(source);
        const from = rows.findIndex(row => row.id === rowId);
        if (from < 0 || from === to)
            return;
        const settled = settleRows(drafts, source, moveRow(rows, from, to));
        const arrowsMoved = settled.order.join() !==
            rows
                .filter(row => !row.draft)
                .map(row => row.id)
                .join();
        snapshot();
        if (arrowsMoved)
            setEdges(es => orderSiblings(es, source, settled.order));
        setDrafts(settled.drafts);
        if (arrowsMoved)
            onDirty();
    };
    // A transition row stands for its arrow: clicking it selects the arrow
    // alone, so Delete cannot cascade into a node.
    const selectArrow = (edgeId) => {
        setNodes(ns => ns.map(n => (n.selected ? { ...n, selected: false } : n)));
        setEdges(es => es.map(e => ({ ...e, selected: e.id === edgeId })));
        revealNodeTab();
    };
    // One drag of a bend grip is one undo step, taken on its first move, so
    // a click that moves nothing changes nothing.
    const bendMoved = useRef(null);
    const bendArrow = (edgeId, change) => {
        if (change === 'start') {
            bendMoved.current = false;
            return;
        }
        if (change === 'end') {
            if (bendMoved.current)
                onDirty();
            bendMoved.current = null;
            return;
        }
        const edge = edges.find(e => e.id === edgeId);
        if (change === null && !edge?.data?.bend) {
            return;
        }
        if (bendMoved.current === false) {
            snapshot();
            bendMoved.current = true;
        }
        setEdges(es => es.map(e => {
            if (e.id !== edgeId)
                return e;
            const { bend: _dropped, ...rest } = (e.data ?? {});
            const data = change ? { ...rest, bend: change } : rest;
            return { ...e, data };
        }));
    };
    // The aside never spills past the editor's right edge, and on a narrow
    // embed (the playground on a phone) it leaves a strip of graph showing
    // so the active node stays in view.
    const rootRef = useRef(null);
    const [rootWidth, setRootWidth] = useState(null);
    useEffect(() => {
        const el = rootRef.current;
        if (!el)
            return;
        const observer = new ResizeObserver(([entry]) => {
            if (entry)
                setRootWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    const asideWidth = rootWidth === null
        ? ASIDE_WIDTH
        : Math.max(0, Math.min(ASIDE_WIDTH, rootWidth - MIN_GRAPH_PEEK));
    // The trigger and the flow starts are pseudo-nodes, so they are not in
    // `steps` and the id would show through. Each is named for what it is.
    const stepTitle = (id) => {
        if (id === TRIGGER_NODE_ID)
            return 'Trigger';
        if (isFlowStartNodeId(id)) {
            // Named the way its pill is, so the panel and the canvas agree.
            const flow = flowStarts.find(f => flowStartNodeId(f.id) === id);
            return flow && flow.events.length > 0
                ? flow.events.map(eventLabel).join(' + ')
                : 'Flow from anywhere';
        }
        return steps.find(s => s.id === id)?.title || id;
    };
    const firstStepTitle = startTarget ? stepTitle(startTarget) : null;
    return (_jsx(FlowMetaContext.Provider, { value: {
            reachable,
            triggerLabel: triggerLabelOf(trigger, availableTriggers),
            triggerChannels: triggerReach.channels,
            triggerChoices: triggerReach.choices,
            fixedEntry,
            channelsReachingNode,
            activeNodeId,
            previousNodeId,
            direction: effectiveDirection,
            implicitSources,
            sourceSlotsByNode,
            targetSlotsByNode,
            edgeHighlightedNodeIds,
            edgeLabels,
            transitionsBySource,
            selectArrow,
            bendArrow,
            draftsBySource,
            deleteArrow,
            moveListRow,
            selectDraft,
            addDraft: source => {
                snapshot();
                setDrafts(ds => [...ds, { id: newId(), source }]);
            },
            removeDraft: draftId => {
                snapshot();
                setDrafts(ds => ds.filter(d => d.id !== draftId));
                if (draftId === selectedDraftId)
                    setSelectedDraftId(null);
            },
            nodeActionGroupsById: nodeActionGroupsById ?? {},
            describeActionGroup,
            transferActionReferencesById: transferActionReferencesById ?? {},
        }, children: _jsxs(motion.div, { ref: rootRef, className: "h-full min-h-0 grid", initial: false, animate: {
                gridTemplateColumns: asideVisible
                    ? `minmax(0, 1fr) ${asideWidth}px`
                    : 'minmax(0, 1fr) 0px',
            }, transition: { duration: 0.26, ease: [0.2, 0.8, 0.2, 1] }, children: [_jsx("div", { className: "min-w-0 min-h-0 overflow-hidden bg-[#E8E8EB]/30 dark:bg-[#2A2A2E]/30", children: _jsxs(ReactFlow, { nodes: nodes, edges: edges, onNodesChange: onNodesChange, onEdgesChange: onEdgesChange, onConnectEnd: (event, state) => {
                            // A drop on a card's body, not its entry dot, still connects.
                            if (state.isValid || !state.fromNode)
                                return;
                            if (state.fromHandle?.type !== 'source')
                                return;
                            const point = 'changedTouches' in event ? event.changedTouches[0] : event;
                            if (!point)
                                return;
                            const target = document
                                .elementFromPoint(point.clientX, point.clientY)
                                ?.closest('.react-flow__node')
                                ?.getAttribute('data-id');
                            if (!target)
                                return;
                            onConnect({
                                source: state.fromNode.id,
                                sourceHandle: state.fromHandle.id ?? null,
                                target,
                                targetHandle: null,
                            });
                        }, onDelete: ({ nodes: gone, edges: cut }) => keepAsDrafts(cut, new Set(gone.map(n => n.id))), onNodeDragStart: onNodeDragStart, onNodeDragStop: onNodeDragStop, onConnect: onConnect, isValidConnection: isValidConnection, onNodeClick: (_, node) => {
                            // The entry pill has its own panel, but it is not a step, so
                            // the type check alone leaves a collapsed inspector shut.
                            if (node.id === TRIGGER_NODE_ID ||
                                isSelectableNodeType(node.type)) {
                                revealNodeTab();
                            }
                        }, onEdgeClick: revealNodeTab, onPaneClick: () => {
                            setSelectedDraftId(null);
                            // Empty-canvas click opens the Graph tab. Hidden-inspector mode
                            // (playground / embedded) keeps the toolbar as the only way in.
                            if (collapsibleInspector)
                                return;
                            setPanelCollapsed(false);
                            setRightTab('graph');
                        }, nodeTypes: nodeTypes, edgeTypes: edgeTypes, colorMode: colorMode, fitView: true, fitViewOptions: { padding: 0.25, maxZoom: 1 }, deleteKeyCode: ['Backspace', 'Delete'], snapToGrid: true, snapGrid: [1, 1], nodeClickDistance: 10, proOptions: { hideAttribution: true }, children: [_jsx(Background, { gap: 12, size: 1 }), _jsx(Controls, { showFitView: false, 
                                // Styled in editor-styles.ts.
                                className: "super-controls", children: _jsx(AutofitMenu, { direction: effectiveDirection, onAutofit: handleAutofit }) }), _jsx(MiniMapCard, {}), _jsx(AutoPan, { activeNodeId: activeNodeId }), _jsx(FocusHandle, { focusStartRef: focusStartRef, focusNodeRef: focusNodeRef }), _jsxs(Panel, { position: "top-left", className: "flex flex-wrap gap-2 max-w-[calc(100%-5rem)]", children: [_jsx(AddNodeMenu, { onAdd: addStep }), _jsx(Button, { type: "button", size: "sm", variant: "outline", className: "h-8 w-8 p-0 shadow-sm", title: "Delete what is selected (or press Delete)", "aria-label": "Delete what is selected", disabled: !hasSelection, onClick: deleteSelected, children: _jsx(Trash2, { className: "h-3.5 w-3.5" }) }), _jsx(Button, { type: "button", size: "sm", variant: "outline", className: "h-8 w-8 p-0 shadow-sm", title: "Undo (Cmd/Ctrl+Z)", "aria-label": "Undo", disabled: !historyStack.canUndo(history), onClick: doUndo, children: _jsx(Undo2, { className: "h-3.5 w-3.5" }) }), _jsx(Button, { type: "button", size: "sm", variant: "outline", className: "h-8 w-8 p-0 shadow-sm", title: "Redo (Cmd/Ctrl+Shift+Z)", "aria-label": "Redo", disabled: !historyStack.canRedo(history), onClick: doRedo, children: _jsx(Redo2, { className: "h-3.5 w-3.5" }) })] }), panelCollapsed && (_jsx(Panel, { position: "top-right", children: _jsx(Button, { type: "button", size: "sm", variant: "outline", className: "h-8 w-8 p-0 shadow-sm", title: "Show inspector", "aria-label": "Show inspector", onClick: () => setPanelCollapsed(false), children: _jsx(PanelRight, { className: "h-3.5 w-3.5" }) }) }))] }) }), _jsx("aside", { className: "min-h-0 min-w-0 border-l bg-background overflow-hidden", "aria-hidden": !asideVisible, children: _jsxs("div", { className: "flex h-full flex-col", style: { width: asideWidth }, children: [_jsxs("div", { className: "sticky top-0 z-10 flex flex-none items-stretch border-b border-border/60 bg-background/90 backdrop-blur", children: [_jsx(TabButton, { active: rightTab === 'graph', onClick: () => setRightTab('graph'), label: "Graph" }), _jsx(TabButton, { active: rightTab === 'node', onClick: () => setRightTab('node'), label: "Node" }), _jsx(TabButton, { active: rightTab === 'test', onClick: () => setRightTab('test'), label: "Test" }), _jsx(Button, { type: "button", size: "sm", variant: "ghost", className: "h-full flex-none rounded-none px-2", title: "Hide inspector", "aria-label": "Hide inspector", onClick: () => setPanelCollapsed(true), children: _jsx(ChevronRight, { className: "h-3.5 w-3.5" }) })] }), _jsx(AnimatePresence, { mode: "wait", initial: false, children: _jsx(motion.div, { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }, className: "min-h-0 flex-1 overflow-y-auto", children: rightTab === 'graph' ? (_jsx("div", { className: "p-4", children: _jsx(ExplainerPanel, { persona: persona, voice: voice, knowledgeBaseIds: knowledgeBaseIds, watchEvents: watchEvents, knowledgeBases: knowledgeBases, firstStepTitle: firstStepTitle, variables: variables, flows: flowRows, onFlows: {
                                                add: addFlowStart,
                                                remove: removeFlowStart,
                                                move: moveFlowStart,
                                                select: focusFlowStart,
                                            }, onChange: patch => {
                                                snapshot();
                                                if (patch.persona !== undefined)
                                                    setPersona(patch.persona);
                                                if (patch.knowledgeBaseIds !== undefined)
                                                    setKnowledgeBaseIds(patch.knowledgeBaseIds);
                                                if (patch.watchEvents !== undefined)
                                                    setWatchEvents(patch.watchEvents);
                                                if (patch.voice !== undefined)
                                                    setVoice(patch.voice);
                                                onDirty();
                                            } }) })) : rightTab === 'test' ? (_jsx("div", { className: "h-full min-h-0 p-4", children: rightPanel ?? (_jsx("p", { className: "text-xs text-muted-foreground", children: "Start a test call to see the transcript and events here." })) })) : entrySelected ? (_jsxs("div", { className: "space-y-4 p-4", children: [_jsxs("div", { className: "space-y-0.5", children: [_jsxs("h3", { className: "flex items-center gap-1.5 text-sm font-medium", children: [_jsx(Zap, { className: "h-3.5 w-3.5" }), "Trigger"] }), _jsx("p", { className: "text-xs text-muted-foreground", children: "Select how to trigger this graph." })] }), _jsx(TriggerPicker, { trigger: trigger, availableTriggers: availableTriggers, onChange: next => {
                                                    snapshot();
                                                    setTrigger(next);
                                                    // A new trigger reaches people a different way and
                                                    // declares its own variables, so neither the channel
                                                    // nor the destination picked against the old one
                                                    // still means anything. Clear both rather than leave
                                                    // a choice the trigger cannot honour.
                                                    setNodes(ns => ns.map(n => n.data?.kind ===
                                                        'start_conversation'
                                                        ? {
                                                            ...n,
                                                            data: {
                                                                ...n.data,
                                                                channels: [],
                                                                destinationVariable: null,
                                                            },
                                                        }
                                                        : n));
                                                    onDirty();
                                                } })] })) : selectedFlowNodeId ? (_jsx("div", { className: "p-4", children: _jsx(FlowStartPanel, { events: flowStarts.find(flow => flowStartNodeId(flow.id) === selectedFlowNodeId)?.events ?? [], watchEvents: watchEvents, targetName: flowTargetName(selectedFlowNodeId), onChange: events => updateFlowStart(selectedFlowNodeId, events), onRemove: () => removeFlowStart(selectedFlowNodeId) }, selectedFlowNodeId) })) : selectedStep ? (_jsx(StepPanel, { nodeId: selectedStep.id, step: dataOf(selectedStep), knowledgeBases: knowledgeBases, graphKnowledgeBaseIds: knowledgeBaseIds, availableScratchpadPaths: availableScratchpadPaths, variables: variables, nodeActionGroupsById: nodeActionGroupsById, onChange: (patch, options) => updateStep(selectedStep.id, patch, options), onNavigateToAdminAction: onNavigateToAdminAction, renderActionInspector: renderActionInspector, renderActionInspectorFooter: renderActionInspectorFooter, renderActionPicker: renderActionPicker, onFetchActionGroupUsage: onFetchActionGroupUsage, onDuplicateActionGroup: onDuplicateActionGroup, onRefreshNodeActionGroup: onRefreshNodeActionGroup, transferActionReferencesById: transferActionReferencesById, onFetchTransferActionUsageCount: onFetchTransferActionUsageCount, onDuplicateTransferActionForNode: onDuplicateTransferActionForNode, onSaveTransferActionParameters: onSaveTransferActionParameters, onRefreshTransferAction: onRefreshTransferAction, onTransferActionDirtyChange: onTransferActionDirtyChange, onTransferEditingValidateChange: onTransferEditingValidateChange, onNavigateToTransferAction: onNavigateToTransferAction, renderTransferEditForm: renderTransferEditForm, availableTransferActions: availableTransferActions, onPickTransferAction: id => applyTransferPick(selectedStep.id, id), onCreateInlineTransferForNode: parameters => applyTransferCreate(selectedStep.id, parameters), describeActionMember: describeActionMember, describeTransferRecipient: describeTransferRecipient })) : selectedEdge && selectedArrow ? (_jsx("div", { className: "p-4", children: _jsx(ArrowPanel, { canvas: canvas, arrow: selectedArrow, sourceTitle: stepTitle(selectedEdge.source), targetTitle: selectedEdge.target === END_NODE_ID
                                                ? 'End call'
                                                : stepTitle(selectedEdge.target), implicit: selectedEdge.source === TRIGGER_NODE_ID
                                                ? 'start'
                                                : isEntryPill(selectedEdge.source)
                                                    ? 'flow'
                                                    : implicitSources.has(selectedEdge.source)
                                                        ? stepById.get(selectedEdge.source)?.type ===
                                                            'end_conversation'
                                                            ? 'end'
                                                            : 'say'
                                                        : undefined, routing: routingOptions, actions: actionChoices, place: {
                                                index: edges
                                                    .filter(e => e.source === selectedEdge.source)
                                                    .findIndex(e => e.id === selectedEdge.id),
                                                count: edges.filter(e => e.source === selectedEdge.source).length,
                                            }, onChange: condition => updateArrow(selectedEdge.id, { condition }), onLabel: label => updateArrow(selectedEdge.id, {
                                                label: label || undefined,
                                            }), onMove: delta => {
                                                snapshot();
                                                setEdges(es => moveAmongSiblings(es, selectedEdge.id, delta));
                                                onDirty();
                                            } }) })) : selectedDraft ? (_jsxs("div", { className: "space-y-3 p-4", children: [_jsx(ArrowPanel, { canvas: canvas, arrow: selectedDraft, sourceTitle: stepTitle(selectedDraft.source), routing: routingOptions, actions: actionChoices, onChange: condition => updateDraft(selectedDraft.id, { condition }), onLabel: label => updateDraft(selectedDraft.id, {
                                                    label: label || undefined,
                                                }) }), _jsx(FieldHelp, { children: "Drag the handle on this row to a node to connect it. A transition that is not connected is not saved." })] })) : (_jsx("div", { className: "p-4", children: _jsx("p", { className: "text-xs text-muted-foreground", children: "Select a node or an edge in the canvas to edit it." }) })) }, rightTab === 'graph'
                                    ? 'graph'
                                    : rightTab === 'test'
                                        ? 'test'
                                        : selectedStep
                                            ? `step:${selectedStep.id}`
                                            : selectedEdge
                                                ? // Key the panel by the group (pair) rather than
                                                    // the specific edge id so adding a sibling edge
                                                    // inside the same group does not retrigger the
                                                    // panel's enter/exit animation.
                                                    `pair:${selectedEdge.source}->${selectedEdge.target}`
                                                : 'node-empty') })] }) })] }) }));
});
//# sourceMappingURL=FlowEditor.js.map